import { createClient } from '@supabase/supabase-js';
import { 
  sendTradeNotificationEmail, 
  extractTradeAssets, 
  getLeagueInfo, 
  getUserEmail 
} from './api/emailNotifications';
import { useUserStore } from './userStore';

const supabaseUrl = 'https://ezjthfrjbqwpxrksijml.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV6anRoZnJqYnF3cHhya3Npam1sIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzIxMzAwNzksImV4cCI6MjA0NzcwNjA3OX0.e3HGapMU19JmJjI52yqAsrNbNOVIxYGbdA5xNNF7RrA';

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

// Auth Supabase (cookies/localStorage internes du SDK) jamais utilisée : login custom via RPC + OIDC/BPM géré à la main.
// Désactivée explicitement pour éviter toute collision avec le ?code=&state= du callback OIDC et des écritures
// localStorage inutiles dans le contexte iframe tiers (Safari ITP / Chrome storage partitioning).
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
});

type NoMovementWindowConfig = {
  nomouvement?: boolean | null;
  nmstarth?: number | null;
  nmendh?: number | null;
  nmstartd?: number | null;
  nmendd?: number | null;
  deadline_demotion_promotion?: string | null;
};

type NoMovementEvaluation = {
  blocked: boolean;
  message?: string;
};

const DAY_NAMES_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const normalizeDayNumber = (day?: number | null): number | null => {
  if (typeof day !== 'number') return null;
  if (day < 1 || day > 7) return null;
  return day;
};

const normalizeMinutes = (minutes?: number | null): number | null => {
  if (typeof minutes !== 'number') return null;
  if (!Number.isInteger(minutes)) return null;
  if (minutes < 0 || minutes >= 1440) return null; // 0 à 1439 minutes (24h * 60)
  return minutes;
};

const formatTimeLabel = (totalMinutes: number) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}h${minutes.toString().padStart(2, '0')}`;
};

const buildNoMovementMessage = (config: Required<{ startDay: number; endDay: number; startMinutes: number; endMinutes: number }>) => {
  const startLabel = `${DAY_NAMES_FR[config.startDay - 1]} ${formatTimeLabel(config.startMinutes)}`;
  const endLabel = `${DAY_NAMES_FR[config.endDay - 1]} ${formatTimeLabel(config.endMinutes)}`;
  return `Les mouvements d'effectif sont actuellement bloqués (${startLabel} → ${endLabel}). Veuillez réessayer après la fin de cette fenêtre.`;
};

const shouldBypassNoMovementWindow = () => {
  try {
    return Boolean(useUserStore.getState().is_impersonating);
  } catch (error) {
    console.warn('Unable to read impersonation state for no-movement window bypass', error);
    return false;
  }
};

const evaluateNoMovementWindow = (
  config?: NoMovementWindowConfig | null,
  referenceDate: Date = new Date()
): NoMovementEvaluation => {
  if (shouldBypassNoMovementWindow()) {
    return { blocked: false };
  }
  if (!config?.nomouvement) {
    return { blocked: false };
  }

  const startDay = normalizeDayNumber(config.nmstartd);
  const endDay = normalizeDayNumber(config.nmendd);
  const startMinutes = normalizeMinutes(config.nmstarth);
  const endMinutes = normalizeMinutes(config.nmendh);

  if (startDay == null || endDay == null || startMinutes == null || endMinutes == null) {
    return { blocked: false };
  }

  // Calculer l'index en minutes depuis le début de la semaine
  const startIndex = (startDay - 1) * 1440 + startMinutes; // 1440 minutes par jour
  const endIndex = (endDay - 1) * 1440 + endMinutes;

  if (startIndex === endIndex) {
    return { blocked: false };
  }

  const jsDay = referenceDate.getDay();
  const currentDay = jsDay === 0 ? 7 : jsDay; // Convertir dimanche (0) en 7
  const currentHour = referenceDate.getHours();
  const currentMinutes = referenceDate.getMinutes();
  const currentIndex = (currentDay - 1) * 1440 + (currentHour * 60) + currentMinutes;

  let blocked = false;
  if (startIndex < endIndex) {
    blocked = currentIndex >= startIndex && currentIndex < endIndex;
  } else {
    blocked = currentIndex >= startIndex || currentIndex < endIndex;
  }

  if (!blocked) {
    return { blocked: false };
  }

  return {
    blocked: true,
    message: buildNoMovementMessage({
      startDay,
      endDay,
      startMinutes,
      endMinutes
    })
  };
};

const evaluatePromotionDeadline = (
  config?: Pick<NoMovementWindowConfig, 'deadline_demotion_promotion'> | null,
  referenceDate: Date = new Date()
): NoMovementEvaluation => {
  if (shouldBypassNoMovementWindow()) {
    return { blocked: false };
  }
  if (!config?.deadline_demotion_promotion) {
    return { blocked: false };
  }
  const deadline = new Date(config.deadline_demotion_promotion);
  if (isNaN(deadline.getTime())) {
    return { blocked: false };
  }
  if (referenceDate <= deadline) {
    return { blocked: false };
  }
  const formattedDate = deadline.toLocaleDateString('fr-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  return {
    blocked: true,
    message: `Les promotions et rétrogradations ne sont plus autorisées depuis le ${formattedDate}.`
  };
};

const fetchNoMovementWindow = async (leagueId: number): Promise<NoMovementWindowConfig | null> => {
  const { data, error } = await supabase
    .from('league_settings')
    .select('nomouvement, nmstarth, nmendh, nmstartd, nmendd')
    .eq('league_id', leagueId)
    .single();

  if (error && error.code !== 'PGRST116') {
    throw error;
  }

  return data ?? null;
};

const ensureNoMovementAllowed = async (leagueId: number): Promise<NoMovementEvaluation> => {
  const config = await fetchNoMovementWindow(leagueId);
  return evaluateNoMovementWindow(config);
};

//** Free agent section Start **

// Fonction pour récupérer les agents libres
export async function getFreeAgents(leagueId: number) {
  try {
    const { data, error } = await supabase
      .rpc('get_free_agents_by_league', { p_league_id: leagueId });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('Supabase error:', error);
    return { data: null, error };
  }
}

// Fonction pour signer un agent libre
export async function signPlayer(
  leagueId: number,
  playerId: number,
  userId: number,
  goingNHL: boolean,
  isScratch: boolean = false,
  fromDraft: boolean = false
) {
  try {
    // 0. Règle spéciale pour les ligues 66 et 69 - Bloquer certains joueurs
    if ((leagueId === 66 || leagueId === 69) && (playerId === 8485702 || playerId === 8473604)) {
      throw new Error('Ce joueur est bloqué par votre commissaire. Veuillez communiquer avec lui pour débloquer.');
    }

    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('max_signing, count_free_agent_signings, nomouvement, nmstarth, nmendh, nmstartd, nmendd')
      .eq('league_id', leagueId)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

    // Par défaut on conserve le comportement historique (compter les FA dans la limite).
    // Seule la valeur explicite `false` désactive le comptage.
    const countFreeAgentSignings = leagueSettings?.count_free_agent_signings !== false;

    if (!fromDraft) {
      const movementCheck = evaluateNoMovementWindow(leagueSettings);
      if (movementCheck.blocked) {
        const error = new Error(movementCheck.message || "Cette action est temporairement bloquée par la fenêtre de non-mouvement de la ligue.");
        (error as any).code = 'NO_MOVEMENT_WINDOW';
        throw error;
      }

      // Ne vérifier la limite de signatures QUE si la ligue compte les FA dans la limite.
      if (countFreeAgentSignings) {
        // Convert max_signing to number and handle null/undefined cases
        const maxSignings = leagueSettings?.max_signing != null ? Number(leagueSettings.max_signing) : 15;

        // Get user's current signing count
        const { data: userLeague, error: userLeagueError } = await supabase
          .from('leagues_users')
          .select('number_of_sigining_used')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .single();

        if (userLeagueError && userLeagueError.code !== 'PGRST116') throw userLeagueError;
        const currentSignings = userLeague?.number_of_sigining_used || 0;

        // Check if signing is allowed (maxSignings > 0) and if user hasn't reached the limit
        if (maxSignings <= 0 || currentSignings >= maxSignings) {
          throw new Error('signing_limit_reached');
        }
      }
    }
    
    // 2. All free-agent signings use the shared RPC, including Arsenal.
    const { data, error } = await supabase.rpc('sign_free_agent', {
      p_goingnhl: goingNHL,
      p_league_id: leagueId,
      p_player_id: playerId,
      p_user_id: userId,
      p_fromfreeagent: !fromDraft
    });

    if (error) throw error;

    // 3. If it's a scratch player, update the league_players_user table
    if (isScratch) {
      const { error: scratchError } = await supabase
        .from('league_players_user')
        .update({ 
          is_scratch: true,
          isnhl: true
        })
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('player_id', playerId);

      if (scratchError) {
        console.error('Error updating scratch status:', scratchError);
        // Don't throw here - the player was still signed successfully
      }
    }
    
    // 4. Only increment the signing counter if NOT from draft AND if free agent
    //    signings are counted against the league limit for this league.
    if (!fromDraft && countFreeAgentSignings) {
      // Get current signing count again (in case it changed)
      const { data: userLeague, error: userLeagueError } = await supabase
        .from('leagues_users')
        .select('number_of_sigining_used')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .single();
      
      if (userLeagueError && userLeagueError.code !== 'PGRST116') throw userLeagueError;
      const currentSignings = userLeague?.number_of_sigining_used || 0;
      
      const { error: updateError } = await supabase
        .from('leagues_users')
        .update({ number_of_sigining_used: currentSignings + 1 })
        .eq('league_id', leagueId)
        .eq('user_id', userId);
      
      if (updateError) throw updateError;
    }
    
    return { data, error: null };
  } catch (error: any) {
    console.error('Error signing player:', error.message);
    throw error;
  }
}

// Fonction pour récupérer les gardiens agents libres
export async function getFreeAgentGoalies(leagueId: number) {
  try {
    console.log("rentre ici");
    const { data, error } = await supabase
      .rpc('get_free_agents_by_league_goalie', { p_league_id: leagueId });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('Supabase error:', error);
    return { data: null, error };
  }
}

//** Standard Flexible Mode – Free agents section Start **

// Agents libres Standard Flexible – attaquants / défenseurs
// Exclut uniquement les joueurs de l'équipe DU user demandeur (non-exclusifs inter-équipes)
export async function getFreeAgentsStandard(leagueId: number, userId: number) {
  try {
    const { data, error } = await supabase
      .rpc('get_free_agents_standard', { p_league_id: leagueId, p_user_id: userId });
    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('[getFreeAgentsStandard] error:', error);
    return { data: null, error };
  }
}

// Agents libres Standard Flexible – gardiens
export async function getFreeAgentsStandardGoalie(leagueId: number, userId: number) {
  try {
    const { data, error } = await supabase
      .rpc('get_free_agents_standard_goalie', { p_league_id: leagueId, p_user_id: userId });
    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('[getFreeAgentsStandardGoalie] error:', error);
    return { data: null, error };
  }
}

export interface StandardPlayerSelectionStats {
  player_id: number;
  selected_completed_teams: number;
  completed_teams_total: number;
  selection_percentage: number;
}

// Standard Flexible – popularité d'un joueur parmi les équipes complétées de la ligue
export async function getStandardPlayerSelectionStats(leagueId: number) {
  try {
    const { data, error } = await supabase
      .rpc('get_standard_player_selection_stats', { p_league_id: leagueId });
    if (error) throw error;
    return { data: data as StandardPlayerSelectionStats[], error: null };
  } catch (error: any) {
    console.error('[getStandardPlayerSelectionStats] error:', error);
    return { data: null, error };
  }
}

// Signer un joueur en mode Standard Flexible
// isInitialBuild = true → phase de construction (gratuit, pas de compteur)
// isInitialBuild = false → phase normale (respecte max_signing + compteur)
export async function signPlayerStandard(
  leagueId: number,
  playerId: number,
  userId: number,
  goingNHL: boolean,
  isInitialBuild: boolean = false
) {
  try {
    let countFreeAgentSignings = true;
    if (!isInitialBuild) {
      // Vérifier la limite de signatures en phase normale
      const { data: leagueSettings, error: settingsError } = await supabase
        .from('league_settings')
        .select('max_signing, count_free_agent_signings, nomouvement, nmstarth, nmendh, nmstartd, nmendd')
        .eq('league_id', leagueId)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

      const movementCheck = evaluateNoMovementWindow(leagueSettings);
      if (movementCheck.blocked) {
        const err = new Error(movementCheck.message || "Cette action est temporairement bloquée par la fenêtre de non-mouvement de la ligue.");
        (err as any).code = 'NO_MOVEMENT_WINDOW';
        throw err;
      }

      // Défaut historique : les FA comptent dans la limite.
      countFreeAgentSignings = leagueSettings?.count_free_agent_signings !== false;

      if (countFreeAgentSignings) {
        const maxSignings = leagueSettings?.max_signing != null ? Number(leagueSettings.max_signing) : 15;

        const { data: userLeague, error: userLeagueError } = await supabase
          .from('leagues_users')
          .select('number_of_sigining_used')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .single();

        if (userLeagueError && userLeagueError.code !== 'PGRST116') throw userLeagueError;
        const currentSignings = userLeague?.number_of_sigining_used || 0;

        if (maxSignings <= 0 || currentSignings >= maxSignings) {
          throw new Error('signing_limit_reached');
        }
      }
    }

    // Signer le joueur (même RPC que le mode Dynasty)
    const { data, error } = await supabase.rpc('sign_free_agent', {
      p_goingnhl: goingNHL,
      p_league_id: leagueId,
      p_player_id: playerId,
      p_user_id: userId,
      p_fromfreeagent: !isInitialBuild,
    });

    if (error) throw error;

    // Incrémenter le compteur seulement en phase normale ET si les FA
    // comptent dans la limite pour cette ligue.
    if (!isInitialBuild && countFreeAgentSignings) {
      const { data: userLeague } = await supabase
        .from('leagues_users')
        .select('number_of_sigining_used')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .single();

      const currentSignings = userLeague?.number_of_sigining_used || 0;

      await supabase
        .from('leagues_users')
        .update({ number_of_sigining_used: currentSignings + 1 })
        .eq('league_id', leagueId)
        .eq('user_id', userId);
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('[signPlayerStandard] error:', error.message);
    throw error;
  }
}

// Terminer la phase de construction (verrouille "Bâtir mon équipe")
export async function completeInitialBuild(leagueId: number, userId: number) {
  try {
    const { error } = await supabase.rpc('complete_initial_build', {
      p_league_id: leagueId,
      p_user_id: userId,
    });
    if (error) throw error;
    return { success: true, error: null };
  } catch (error: any) {
    console.error('[completeInitialBuild] error:', error.message);
    return { success: false, error };
  }
}

//** Standard Flexible Mode – Free agents section End **

//** Watchlist section Start **

//** Pooler follows section Start **

export async function getFollowedPoolerIds(leagueId: number, followerUserId: number) {
  try {
    const { data, error } = await supabase
      .from('pooler_follows')
      .select('followed_user_id')
      .eq('league_id', leagueId)
      .eq('follower_user_id', followerUserId);

    if (error && error.code !== 'PGRST116') throw error;

    return { data: data?.map((item) => Number(item.followed_user_id)) || [], error: null };
  } catch (error: any) {
    console.error('Error getting followed pooler IDs:', error);
    return { data: [], error };
  }
}

export async function getFollowedPoolerIdSet(leagueId: number, followerUserId: number) {
  const result = await getFollowedPoolerIds(leagueId, followerUserId);
  return { data: new Set(result.data), error: result.error };
}

export async function followPooler(
  leagueId: number,
  followerUserId: number,
  followedUserId: number
) {
  try {
    if (Number(followerUserId) === Number(followedUserId)) {
      return {
        success: false,
        error: { message: 'Vous ne pouvez pas vous suivre vous-même' }
      };
    }

    const { error } = await supabase
      .from('pooler_follows')
      .insert({
        league_id: leagueId,
        follower_user_id: followerUserId,
        followed_user_id: followedUserId
      });

    if (error) {
      if (error.code === '23505') {
        return { success: true };
      }
      throw error;
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error following pooler:', error);
    return {
      success: false,
      error: { message: 'Erreur lors du suivi du pooler' }
    };
  }
}

export async function unfollowPooler(
  leagueId: number,
  followerUserId: number,
  followedUserId: number
) {
  try {
    const { error } = await supabase
      .from('pooler_follows')
      .delete()
      .eq('league_id', leagueId)
      .eq('follower_user_id', followerUserId)
      .eq('followed_user_id', followedUserId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error unfollowing pooler:', error);
    return {
      success: false,
      error: { message: 'Erreur lors du retrait du suivi' }
    };
  }
}

//** Pooler follows section End **

// Fonction pour ajouter un joueur à la watchlist
export async function addToWatchlist(
  leagueId: number,
  userId: number,
  playerId: number,
  playerName: string,
  playerPosition: string,
  isGoalie: boolean,
  salary?: number,
  teamAbbrev?: string
) {
  try {
    const { data, error } = await supabase
      .from('player_watchlist')
      .insert({
        league_id: leagueId,
        user_id: userId,
        player_id: playerId,
        player_name: playerName,
        player_position: playerPosition,
        is_goalie: isGoalie,
        salary: salary || null,
        team_abbrev: teamAbbrev || null
      });

    if (error) {
      // Check for duplicate entry
      if (error.code === '23505') {
        return {
          success: false,
          error: { message: 'Ce joueur est déjà dans votre liste de surveillance' }
        };
      }
      throw error;
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('Error adding to watchlist:', error);
    return {
      success: false,
      error: { message: 'Erreur lors de l\'ajout à la liste de surveillance' }
    };
  }
}

// Fonction pour retirer un joueur de la watchlist
export async function removeFromWatchlist(
  leagueId: number,
  userId: number,
  playerId: number
) {
  try {
    const { error } = await supabase
      .from('player_watchlist')
      .delete()
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('player_id', playerId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error removing from watchlist:', error);
    return {
      success: false,
      error: { message: 'Erreur lors du retrait de la liste de surveillance' }
    };
  }
}

// Fonction pour récupérer les joueurs de la watchlist d'un utilisateur
export async function getWatchlistPlayers(leagueId: number, userId: number) {
  try {
    // Première requête : récupérer les IDs des joueurs dans la watchlist
    const { data: watchlistData, error: watchlistError } = await supabase
      .from('player_watchlist')
      .select('id, player_id, created_at')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (watchlistError && watchlistError.code !== 'PGRST116') throw watchlistError;
    
    if (!watchlistData || watchlistData.length === 0) {
      return { data: [], error: null };
    }

    // Extraire les IDs des joueurs
    const playerIds = watchlistData.map(item => item.player_id);

    // Deuxième requête : récupérer les informations des joueurs (attaquants/défenseurs)
    const { data: playersData, error: playersError } = await supabase
      .from('players')
      .select('playerid, skaterfullname, positioncode, salary, teamabbrevs')
      .in('playerid', playerIds);

    if (playersError && playersError.code !== 'PGRST116') throw playersError;

    // Troisième requête : récupérer les informations des gardiens
    const { data: goaliesData, error: goaliesError } = await supabase
      .from('goalies')
      .select('playerid, goaliefullname, positioncode, salary, teamabbrevs')
      .in('playerid', playerIds);

    if (goaliesError && goaliesError.code !== 'PGRST116') throw goaliesError;

    // Quatrième requête : récupérer les propriétaires des joueurs depuis league_players_user
    const { data: ownersData, error: ownersError } = await supabase
      .from('league_players_user')
      .select('player_id, user_id')
      .eq('league_id', leagueId)
      .in('player_id', playerIds);

    if (ownersError && ownersError.code !== 'PGRST116') throw ownersError;

    // Cinquième requête : récupérer les noms des utilisateurs propriétaires depuis la table users
    const ownerUserIds = [...new Set((ownersData || []).map(owner => owner.user_id))];
    let usersData: { user_id: number; username: string; }[] = [];
    if (ownerUserIds.length > 0) {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('user_id, username')
        .in('user_id', ownerUserIds);

      if (userError && userError.code !== 'PGRST116') throw userError;
      usersData = (userData || []).map(user => ({ user_id: user.user_id, username: user.username }));
    }

    // Combiner les données des deux tables
    const allPlayersData = [
      ...(playersData || []),
      ...(goaliesData?.map(goalie => ({
        ...goalie,
        skaterfullname: goalie.goaliefullname // Normaliser le nom pour la logique commune
      })) || [])
    ];

    // Combiner les données
    const transformedData = watchlistData.map(watchlistItem => {
      const playerInfo = allPlayersData?.find(p => p.playerid === watchlistItem.player_id);
      const positionCode = playerInfo?.positioncode || 'N/A';
      
      // Trouver le propriétaire du joueur
      const ownerInfo = ownersData?.find(owner => owner.player_id === watchlistItem.player_id);
      const ownerUser = ownerInfo ? usersData.find(user => user.user_id === ownerInfo.user_id) : null;
      console.log('Owner User:', ownerUser);  
      return {
        id: watchlistItem.id,
        player_id: watchlistItem.player_id,
        player_name: playerInfo?.skaterfullname || 'Joueur inconnu',
        player_position: positionCode,
        is_goalie: positionCode === 'G' || positionCode === 'Goalie' || positionCode.toLowerCase().includes('g'),
        salary: playerInfo?.salary || 0,
        team_abbrev: playerInfo?.teamabbrevs || 'N/A',
        created_at: watchlistItem.created_at,
        owner_name: ownerUser?.username || null,
        is_free_agent: !ownerInfo
      };
    });

    return { data: transformedData, error: null };
  } catch (error: any) {
    console.error('Error getting watchlist players:', error);
    return { data: [], error };
  }
}

// Fonction pour vérifier si un joueur est dans la watchlist
export async function isPlayerInWatchlist(
  leagueId: number,
  userId: number,
  playerId: number
) {
  try {
    const { data, error } = await supabase
      .from('player_watchlist')
      .select('id')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('player_id', playerId)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') throw error;
    return { isInWatchlist: !!data, error: null };
  } catch (error: any) {
    console.error('Error checking if player is in watchlist:', error);
    return { isInWatchlist: false, error };
  }
}

// Fonction pour obtenir les IDs des joueurs en watchlist (pour optimiser l'affichage)
export async function getWatchlistPlayerIds(leagueId: number, userId: number) {
  try {
    const { data, error } = await supabase
      .from('player_watchlist')
      .select('player_id')
      .eq('league_id', leagueId)
      .eq('user_id', userId);

    if (error && error.code !== 'PGRST116') throw error;
    const playerIds = data?.map(item => item.player_id) || [];
    return { data: playerIds, error: null };
  } catch (error: any) {
    console.error('Error getting watchlist player IDs:', error);
    return { data: [], error };
  }
}

//** Watchlist section End **

//** Free agent section End **

//** MyTeam section start **

// Fonction pour récupérer les informations de rétrogradation
export async function getDemotionInfo(
  leagueId: number,
  userId: number
) {
  try {
    // 1. Récupérer les paramètres de la ligue pour connaître la limite de rétrogradations
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('max_demotion')
      .eq('league_id', leagueId)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;
    
    const maxDemotions = leagueSettings?.max_demotion || 10; // Valeur par défaut si non définie
    
    // 2. Vérifier combien de rétrogradations ont déjà été utilisées
    const { data: userLeague, error: userLeagueError } = await supabase
      .from('leagues_users')
      .select('number_of_demotion_used')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .single();
    
    if (userLeagueError && userLeagueError.code !== 'PGRST116') throw userLeagueError;
    
    const currentDemotions = userLeague?.number_of_demotion_used || 0;
    
    return { 
      success: true,
      data: {
        current: currentDemotions,
        max: maxDemotions
      }
    };
  } catch (error: any) {
    console.error('Error getting demotion info:', error);
    return { success: false, error };
  }
}

// Fonction pour récupérer les informations de rétrogradation
export async function getMaxSigningInfo(
  leagueId: number,
  userId: number
) {
  try {
    // 1. Récupérer les paramètres de la ligue pour connaître la limite de signature
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('max_signing')
      .eq('league_id', leagueId)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;
    
    // Respecter une valeur explicite de 0 dans `max_signing`.
    // Seule la valeur `null`/`undefined` doit déclencher la valeur par défaut.
    const maxSignings = leagueSettings?.max_signing != null ? Number(leagueSettings.max_signing) : 10; // Valeur par défaut si non définie
    
    // 2. Vérifier combien de signature ont déjà été utilisées
    const { data: userLeague, error: userLeagueError } = await supabase
      .from('leagues_users')
      .select('number_of_sigining_used')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .single();
    
    if (userLeagueError && userLeagueError.code !== 'PGRST116') throw userLeagueError;
    
    const currentSignings = userLeague?.number_of_sigining_used != null ? Number(userLeague.number_of_sigining_used) : 0;
    
    return { 
      success: true,
      data: {
        current: currentSignings,
        max: maxSignings
      }
    };
  } catch (error: any) {
    console.error('Error getting signings info:', error);
    return { success: false, error };
  }
}


// Fonction pour récupérer les joueur de mon equipe
export async function getMyteamPlayer(leagueId: number, userId: number) {
  try {
    // Premier appel pour récupérer les données des joueurs
    const { data: playersData, error: playersError } = await supabase
      .from('getmyteamplayer')
      .select('*')
      .eq('league_id', leagueId)
      .eq('user_id', userId);

    if (playersError) throw playersError;

    // Deuxième appel pour récupérer les statuts trading_bloc et is_intouchable
    const { data: statusData, error: statusError } = await supabase
      .from('league_players_user')
      .select('player_id, trading_bloc, is_intouchable')
      .eq('league_id', leagueId)
      .eq('user_id', userId);

    if (statusError) throw statusError;

    // Fusionner les données
    const mergedData = playersData?.map(player => {
      const status = statusData?.find(s => s.player_id === player.player_id);
      return {
        ...player,
        trading_bloc: status?.trading_bloc || null,
        is_intouchable: status?.is_intouchable || null
      };
    });

    return { data: mergedData, error: null };
  } catch (error: any) {
    console.error('Supabase error:', error);
    return { data: null, error };
  }
}

// Fonction pour récupérer les gardiens de mon équipe
export async function getMyTeamGoalies(leagueId: number, userId: number) {
  try {
    // Premier appel pour récupérer les données des gardiens
    const { data: goaliesData, error: goaliesError } = await supabase
      .from('getmyteamgoalie')
      .select('*')
      .eq('league_id', leagueId)
      .eq('user_id', userId);

    if (goaliesError) throw goaliesError;

    // Deuxième appel pour récupérer les statuts trading_bloc et is_intouchable
    const { data: statusData, error: statusError } = await supabase
      .from('league_players_user')
      .select('player_id, trading_bloc, is_intouchable')
      .eq('league_id', leagueId)
      .eq('user_id', userId);

    if (statusError) throw statusError;

    // Fusionner les données
    const mergedData = goaliesData?.map(goalie => {
      const status = statusData?.find(s => s.player_id === goalie.player_id);
      return {
        ...goalie,
        trading_bloc: status?.trading_bloc || null,
        is_intouchable: status?.is_intouchable || null
      };
    });

    return { data: mergedData, error: null };
  } catch (error: any) {
    console.error('Supabase error:', error);
    return { data: null, error };
  }
}

// Recherche de joueurs (patineurs et gardiens) dans toute la ligue (toutes les équipes)
// Permet de trouver à qui appartient un joueur par son nom partiel (min 3 caractères recommandé côté appelant)
export async function searchLeaguePlayers(leagueId: number, term: string) {
  try {
    const cleaned = term.trim();
    if (!cleaned) return { data: [], error: null };

    // Requête parallèle sur les vues des joueurs et gardiens
    const [skatersRes, goaliesRes] = await Promise.all([
      supabase
        .from('getmyteamplayer')
        .select('playerid, skaterfullname, user_id')
        .eq('league_id', leagueId)
        .ilike('skaterfullname', `%${cleaned}%`)
        .limit(15),
      supabase
        .from('getmyteamgoalie')
        .select('playerid, goaliefullname, user_id')
        .eq('league_id', leagueId)
        .ilike('goaliefullname', `%${cleaned}%`)
        .limit(15)
    ]);

    const skatersError = (skatersRes as any).error;
    if (skatersError && skatersError.code !== 'PGRST116') throw skatersError;
    const goaliesError = (goaliesRes as any).error;
    if (goaliesError && goaliesError.code !== 'PGRST116') throw goaliesError;

    const skaters = (skatersRes as any).data?.map((p: any) => ({
      playerid: p.playerid,
      fullname: p.skaterfullname,
      user_id: p.user_id,
      isGoalie: false
    })) || [];

    const goalies = (goaliesRes as any).data?.map((g: any) => ({
      playerid: g.playerid,
      fullname: g.goaliefullname,
      user_id: g.user_id,
      isGoalie: true
    })) || [];

    // Fusionner et dédupliquer (au cas où un id se retrouverait dans les deux, par sécurité)
    const merged = [...skaters, ...goalies];
    const uniqueMap = new Map<number, any>();
    for (const item of merged) {
      if (!uniqueMap.has(item.playerid)) {
        uniqueMap.set(item.playerid, item);
      }
    }

    return { data: Array.from(uniqueMap.values()), error: null };
  } catch (error: any) {
    console.error('Supabase searchLeaguePlayers error:', error);
    return { data: null, error };
  }
}

export async function getTeamIdByTeamName(leagueId: number, teamName: string) {
  try {
    const cleanedTeamName = teamName.trim();
    const { data, error } = await supabase
      .from('getteamidbyteamname')
      .select('*')
      .eq('league_id', leagueId)
      .eq('username', cleanedTeamName);

    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('Supabase error:', error);
    return { data: null, error };
  }
}

//Fonction pour retirer un joueur de mon equipe
// isInitialBuild = true (BuildMyTeam) : retrait pendant la construction initiale, non historisé.
// Sinon, la RPC release_player retire le joueur et l'inscrit dans freeagentsreleasehistory
// dans la même transaction.
export async function releasePlayer(
  playerId: number,
  userId: number,
  leagueId: number,
  isInitialBuild: boolean = false
) {
  try {
    const movementCheck = await ensureNoMovementAllowed(leagueId);
    if (movementCheck.blocked) {
      return {
        success: false,
        error: { message: movementCheck.message || "Les mouvements d'effectif sont actuellement bloqués." }
      };
    }    

    const { data, error } = isInitialBuild
      ? await supabase
          .from('league_players_user')
          .delete()
          .eq('player_id', playerId)
          .eq('user_id', userId)
          .eq('league_id', leagueId)
      : await supabase.rpc('release_player', {
          p_player_id: playerId,
          p_user_id: userId,
          p_league_id: leagueId
        });

    if (error) {
      throw error;
    }

    console.log('Player successfully released:', data);
    return { success: true, data };
  } catch (error: any) {
    console.error('Error releasing player:', error);
    return { success: false, error };
  }
}

// Fonction pour basculer un joueur entre l'équipe principale et les réserves
export async function togglePlayerReserveStatus(
  playerId: number,
  userId: number,
  leagueId: number,
  isNHL: boolean
) {
  try {
    // Récupérer les paramètres de la ligue pour les vérifications de plafond salarial et limites d'effectif
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('max_demotion, salary_cap, max_salary_cap, salary_cap_reserve, max_salary_cap_reserve, number_foward, number_def, number_goalie, number_reserve, number_reserve_protect, number_foward_protect, number_def_protect, number_goalie_protect, protectionlist, nomouvement, nmstarth, nmendh, nmstartd, nmendd, deadline_demotion_promotion')
      .eq('league_id', leagueId)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

    const movementCheck = evaluateNoMovementWindow(leagueSettings);
    if (movementCheck.blocked) {
      return {
        success: false,
        error: { message: movementCheck.message || "Les mouvements d'effectif sont actuellement bloqués." }
      };
    }

    const deadlineCheck = evaluatePromotionDeadline(leagueSettings);
    if (deadlineCheck.blocked) {
      return {
        success: false,
        error: { message: deadlineCheck.message || "Les promotions et rétrogradations ne sont plus autorisées." }
      };
    }

    // Déterminer le salaire du joueur
    let playerSalary = 0;
    
    // Essayer d'abord dans la table des joueurs
    const { data: playerData } = await supabase
      .from('getmyteamplayer')
      .select('salary')
      .eq('playerid', playerId)
      .eq('user_id', userId)
      .eq('league_id', leagueId)
      .single();

    if (playerData) {
      playerSalary = playerData.salary;
    } else {
      // Si pas trouvé dans les joueurs, chercher dans les gardiens
      const { data: goalieData } = await supabase
        .from('getmyteamgoalie')
        .select('salary')
        .eq('playerid', playerId)
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .single();

      if (goalieData) {
        playerSalary = goalieData.salary;
      } else {
        throw new Error('Player not found in team');
      }
    }

    // Si c'est une rétrogradation (du NHL aux réserves)
    if (isNHL) {
      const maxDemotions = leagueSettings?.max_demotion || 10; // Valeur par défaut si non définie
      
      // Vérifier combien de rétrogradations ont déjà été utilisées
      const { data: userLeague, error: userLeagueError } = await supabase
        .from('leagues_users')
        .select('number_of_demotion_used')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .single();
      
      if (userLeagueError && userLeagueError.code !== 'PGRST116') throw userLeagueError;
      
      const currentDemotions = userLeague?.number_of_demotion_used || 0;
      
      // Vérifier si la limite de rétrogradation est atteinte
      if (currentDemotions >= maxDemotions) {
        return { 
          success: false, 
          error: { message: 'Demotion limit reached' },
          demotionLimit: {
            current: currentDemotions,
            max: maxDemotions
          }
        };
      }

      // Vérifier la limite de joueurs de réserve si définie (pour les rétrogradations)
      const maxReservePlayers = leagueSettings?.number_reserve;
      if (maxReservePlayers && maxReservePlayers > 0) {
        // Déterminer si c'est un gardien ou un joueur
        let isGoalie = false;
        
        // Essayer d'abord dans la table des joueurs
        const { data: playerPositionData } = await supabase
          .from('getmyteamplayer')
          .select('positioncode')
          .eq('playerid', playerId)
          .eq('user_id', userId)
          .eq('league_id', leagueId)
          .single();

        if (!playerPositionData) {
          // Si pas trouvé dans les joueurs, c'est un gardien
          isGoalie = true;
        }

        // Compter les joueurs actuels en réserve
        if (isGoalie) {
          const { data: currentReserveGoalies } = await supabase
            .from('get_number_type_goalie_per_team')
            .select('number_of_goalie_reserve')
            .eq('league_id', leagueId)
            .eq('user_id', userId)
            .single();

          const currentReserveGoaliesCount = currentReserveGoalies?.number_of_goalie_reserve || 0;
          
          if (currentReserveGoaliesCount >= maxReservePlayers) {
            return {
              success: false,
              error: { message: `Limite de gardiens de réserve atteinte (${currentReserveGoaliesCount}/${maxReservePlayers})` }
            };
          }
        } else {
          const { data: currentReservePlayers } = await supabase
            .from('get_number_type_player_per_team')
            .select('number_of_defenders_reserve, number_of_fowards_reserve')
            .eq('league_id', leagueId)
            .eq('user_id', userId)
            .single();

          const currentReservePlayersCount = (currentReservePlayers?.number_of_defenders_reserve || 0) + 
                                           (currentReservePlayers?.number_of_fowards_reserve || 0);
          
          if (currentReservePlayersCount >= maxReservePlayers) {
            return {
              success: false,
              error: { message: `Limite de joueurs de réserve atteinte (${currentReservePlayersCount}/${maxReservePlayers})` }
            };
          }
        }
      }

      // Vérifier le plafond salarial des réserves si activé (pour les rétrogradations)
      if (leagueSettings?.salary_cap_reserve && leagueSettings?.max_salary_cap_reserve) {
        const { data: currentReserveCap, error: reserveCapError } = await supabase
          .from('get_salary_cap_per_team')
          .select('salary_cap')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .eq('isnhl', false)
          .single();

        if (reserveCapError && reserveCapError.code !== 'PGRST116') throw reserveCapError;

        const currentReserveSalaryCap = currentReserveCap?.salary_cap || 0;
        const newReserveSalaryCap = currentReserveSalaryCap + playerSalary;

        if (newReserveSalaryCap > leagueSettings.max_salary_cap_reserve) {
          const excess = newReserveSalaryCap - leagueSettings.max_salary_cap_reserve;
          return {
            success: false,
            error: { message: `Reserve salary cap would be exceeded by $${excess.toLocaleString()}` }
          };
        }
      }

      // Vérifier si la rétrogradation dépasserait la limite de réservistes protégés
      if (leagueSettings?.protectionlist) {
        const maxReserveProtect = leagueSettings?.number_reserve_protect || 0;
        if (maxReserveProtect > 0) {
          const { data: protectionEntry } = await supabase
            .from('protection_list')
            .select('playerid')
            .eq('playerid', playerId)
            .eq('user_id', userId)
            .eq('league_id', leagueId)
            .maybeSingle();

          if (protectionEntry) {
            // Le joueur est protégé — compter combien de protégés sont déjà en réserve
            const { data: allProtected } = await supabase
              .from('protection_list')
              .select('playerid')
              .eq('user_id', userId)
              .eq('league_id', leagueId);

            const protectedIds = (allProtected || []).map((p: any) => p.playerid);

            const { data: reserveProtected } = await supabase
              .from('league_players_user')
              .select('player_id')
              .eq('user_id', userId)
              .eq('league_id', leagueId)
              .eq('isnhl', false)
              .in('player_id', protectedIds);

            const currentReserveProtectedCount = (reserveProtected || []).length;
            if (currentReserveProtectedCount >= maxReserveProtect) {
              return {
                success: false,
                error: { message: `Impossible de rétrograder ce joueur — il est dans votre liste de protection et la limite de réservistes protégés est atteinte (${currentReserveProtectedCount}/${maxReserveProtect}). Retirez-le d'abord de la liste de protection avant d'effectuer ce mouvement.` }
              };
            }
          }
        }
      }
      
      // Incrémenter le compteur de rétrogradations
      const { error: updateDemotionError } = await supabase
        .from('leagues_users')
        .update({ number_of_demotion_used: currentDemotions + 1 })
        .eq('league_id', leagueId)
        .eq('user_id', userId);
      
      if (updateDemotionError) throw updateDemotionError;
    } else {
      // Si c'est une promotion (des réserves vers le NHL), vérifier les limites d'effectif
      
      // Déterminer la position du joueur et s'il s'agit d'un gardien
      let playerPosition = '';
      let isGoalie = false;
      
      // Essayer d'abord dans la table des joueurs
      const { data: playerPositionData } = await supabase
        .from('getmyteamplayer')
        .select('positioncode')
        .eq('playerid', playerId)
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .single();

      if (playerPositionData) {
        playerPosition = playerPositionData.positioncode;
      } else {
        // Si pas trouvé dans les joueurs, c'est un gardien
        isGoalie = true;
      }

      // Compter les joueurs actuels dans l'équipe principale par position en utilisant les vues qui excluent les scratch
      if (isGoalie) {
        // Utiliser la vue get_number_type_goalie_per_team qui exclut déjà les gardiens scratch
        const { data: goalieRosterCounts, error: goalieRosterError } = await supabase
          .from('get_number_type_goalie_per_team')
          .select('number_of_goalie')
          .eq('user_id', userId)
          .eq('league_id', leagueId)
          .single();

        if (goalieRosterError && goalieRosterError.code !== 'PGRST116') throw goalieRosterError;

        const currentGoaliesCount = goalieRosterCounts?.number_of_goalie || 0;
        const maxGoalies = leagueSettings?.number_goalie || 2;
        if (currentGoaliesCount + 1 > maxGoalies) {
          return {
            success: false,
            error: { message: `Limite de gardiens dans l'équipe principale atteinte (${currentGoaliesCount}/${maxGoalies})` }
          };
        }
      } else {
        // Utiliser la vue get_number_type_player_per_team qui exclut déjà les joueurs scratch
        const { data: rosterCounts, error: rosterError } = await supabase
          .from('get_number_type_player_per_team')
          .select('number_of_defenders, number_of_fowards')
          .eq('user_id', userId)
          .eq('league_id', leagueId)
          .single();

        if (rosterError && rosterError.code !== 'PGRST116') throw rosterError;

        const currentDefensemen = rosterCounts?.number_of_defenders || 0;
        const currentForwards = rosterCounts?.number_of_fowards || 0;

        if (playerPosition === 'D') {
          const maxDefensemen = leagueSettings?.number_def || 6;
          if (currentDefensemen + 1 > maxDefensemen) {
            return {
              success: false,
              error: { message: `Limite de défenseurs dans l'équipe principale atteinte (${currentDefensemen}/${maxDefensemen})` }
            };
          }
        } else if (['C', 'L', 'R'].includes(playerPosition)) {
          const maxForwards = leagueSettings?.number_foward || 12;
          if (currentForwards + 1 > maxForwards) {
            return {
              success: false,
              error: { message: `Limite d'attaquants dans l'équipe principale atteinte (${currentForwards}/${maxForwards})` }
            };
          }
        }
      }

      // Si c'est une promotion (des réserves vers le NHL), vérifier le plafond salarial du club principal
      if (leagueSettings?.salary_cap && leagueSettings?.max_salary_cap) {
        const { data: currentMainCap, error: mainCapError } = await supabase
          .from('get_salary_cap_per_team')
          .select('salary_cap')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .eq('isnhl', true)
          .single();

        if (mainCapError && mainCapError.code !== 'PGRST116') throw mainCapError;

        const currentMainSalaryCap = currentMainCap?.salary_cap || 0;
        const newMainSalaryCap = currentMainSalaryCap + playerSalary;

        if (newMainSalaryCap > leagueSettings.max_salary_cap) {
          const excess = newMainSalaryCap - leagueSettings.max_salary_cap;
          return {
            success: false,
            error: { message: `Main club salary cap would be exceeded by $${excess.toLocaleString()}` }
          };
        }
      }

      // Vérifier si la promotion d'un joueur protégé dépasserait la limite de protection par position
      if (leagueSettings?.protectionlist) {
        const { data: protectionEntry } = await supabase
          .from('protection_list')
          .select('playerid')
          .eq('playerid', playerId)
          .eq('user_id', userId)
          .eq('league_id', leagueId)
          .maybeSingle();

        if (protectionEntry) {
          // Le joueur est protégé comme réserviste — après promotion il comptera dans les limites NHL
          const { data: allProtected } = await supabase
            .from('protection_list')
            .select('playerid')
            .eq('user_id', userId)
            .eq('league_id', leagueId);

          const protectedIds = (allProtected || []).map((p: any) => p.playerid);

          // Compter les protégés actuels dans le club principal (isnhl=true) par position
          const { data: mainProtected } = await supabase
            .from('league_players_user')
            .select('player_id')
            .eq('user_id', userId)
            .eq('league_id', leagueId)
            .eq('isnhl', true)
            .in('player_id', protectedIds);

          if (isGoalie) {
            // Compter les gardiens protégés dans le club principal
            const { data: mainProtectedGoalies } = await supabase
              .from('getmyteamgoalie')
              .select('playerid')
              .eq('user_id', userId)
              .eq('league_id', leagueId)
              .eq('isnhl', true)
              .in('playerid', protectedIds);

            const currentProtectedGoalies = (mainProtectedGoalies || []).length;
            const maxProtectedGoalies = leagueSettings?.number_goalie_protect || 1;
            if (currentProtectedGoalies >= maxProtectedGoalies) {
              return {
                success: false,
                error: { message: `Impossible de promouvoir ce gardien — il est dans votre liste de protection et la limite de gardiens protégés dans le club principal est atteinte (${currentProtectedGoalies}/${maxProtectedGoalies}). Retirez-le d'abord de la liste de protection avant d'effectuer ce mouvement.` }
              };
            }
          } else {
            // Récupérer les positions des joueurs protégés dans le club principal
            const mainProtectedPlayerIds = (mainProtected || []).map((p: any) => p.player_id);
            const { data: mainProtectedPlayers } = await supabase
              .from('getmyteamplayer')
              .select('playerid, positioncode')
              .eq('user_id', userId)
              .eq('league_id', leagueId)
              .eq('isnhl', true)
              .in('playerid', mainProtectedPlayerIds);

            if (playerPosition === 'D') {
              const currentProtectedDef = (mainProtectedPlayers || []).filter((p: any) => p.positioncode === 'D').length;
              const maxProtectedDef = leagueSettings?.number_def_protect || 3;
              if (currentProtectedDef >= maxProtectedDef) {
                return {
                  success: false,
                  error: { message: `Impossible de promouvoir ce défenseur — il est dans votre liste de protection et la limite de défenseurs protégés dans le club principal est atteinte (${currentProtectedDef}/${maxProtectedDef}). Retirez-le d'abord de la liste de protection avant d'effectuer ce mouvement.` }
                };
              }
            } else if (['C', 'L', 'R'].includes(playerPosition)) {
              const currentProtectedFwd = (mainProtectedPlayers || []).filter((p: any) => ['C', 'L', 'R'].includes(p.positioncode)).length;
              const maxProtectedFwd = leagueSettings?.number_foward_protect || 8;
              if (currentProtectedFwd >= maxProtectedFwd) {
                return {
                  success: false,
                  error: { message: `Impossible de promouvoir cet attaquant — il est dans votre liste de protection et la limite d'attaquants protégés dans le club principal est atteinte (${currentProtectedFwd}/${maxProtectedFwd}). Retirez-le d'abord de la liste de protection avant d'effectuer ce mouvement.` }
                };
              }
            }
          }
        }
      }
    }

    // 5. Mettre à jour le statut du joueur
    const updateData: any = { isnhl: !isNHL };
    
    // Si c'est une rétrogradation (du NHL aux réserves), vérifier si le joueur est scratch
    // et mettre à jour is_scratch à false pour l'envoyer dans les réserves normales
    if (isNHL) {
      // Vérifier si le joueur est actuellement scratch
      const { data: playerStatus, error: statusError } = await supabase
        .from('league_players_user')
        .select('is_scratch')
        .eq('player_id', playerId)
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .single();

      if (!statusError && playerStatus?.is_scratch) {
        // Si le joueur est scratch et qu'on le rétrograde, le mettre is_scratch à false
        updateData.is_scratch = false;
      }
    }

    const { data, error } = await supabase
      .from('league_players_user')
      .update(updateData)
      .eq('player_id', playerId)
      .eq('user_id', userId)
      .eq('league_id', leagueId);

    if (error) throw error;
    return { success: true, data };
  } catch (error: any) {
    console.error('Error toggling player reserve status:', error);
    return { success: false, error };
  }
}

// Fonction pour échanger deux joueurs entre l'équipe principale et les réserves
export async function swapPlayers(
  leagueId: number,
  userId: number,
  mainPlayerInfo: { id: number; isGoalie: boolean },
  reservePlayerInfo: { id: number; isGoalie: boolean }
) {
  try {
    // 1. Vérifier la limite de rétrogradation car le joueur du club principal est rétrogradé
    // Récupérer les paramètres de la ligue pour connaître la limite de rétrogradations
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('max_demotion, salary_cap, max_salary_cap, salary_cap_reserve, max_salary_cap_reserve, protectionlist, number_reserve_protect, number_foward_protect, number_def_protect, number_goalie_protect, nomouvement, nmstarth, nmendh, nmstartd, nmendd, deadline_demotion_promotion')
      .eq('league_id', leagueId)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

    const movementCheck = evaluateNoMovementWindow(leagueSettings);
    if (movementCheck.blocked) {
      return {
        success: false,
        error: { message: movementCheck.message || "Les mouvements d'effectif sont actuellement bloqués." }
      };
    }

    const deadlineCheck = evaluatePromotionDeadline(leagueSettings);
    if (deadlineCheck.blocked) {
      return {
        success: false,
        error: { message: deadlineCheck.message || "Les promotions et rétrogradations ne sont plus autorisées." }
      };
    }
    
    const maxDemotions = leagueSettings?.max_demotion || 10; // Valeur par défaut si non définie
    
    // Vérifier combien de rétrogradations ont déjà été utilisées
    const { data: userLeague, error: userLeagueError } = await supabase
      .from('leagues_users')
      .select('number_of_demotion_used')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .single();
    
    if (userLeagueError && userLeagueError.code !== 'PGRST116') throw userLeagueError;
    
    const currentDemotions = userLeague?.number_of_demotion_used || 0;
    
    // Vérifier si la limite est atteinte
    if (currentDemotions >= maxDemotions) {
      return { 
        success: false, 
        error: { message: 'Demotion limit reached' },
        demotionLimit: {
          current: currentDemotions,
          max: maxDemotions
        }
      };
    }

    // Récupérer les salaires des deux joueurs
    const mainPlayerTable = mainPlayerInfo.isGoalie ? 'getmyteamgoalie' : 'getmyteamplayer';
    const { data: mainPlayerData, error: mainPlayerDataError } = await supabase
      .from(mainPlayerTable)
      .select('salary')
      .eq('playerid', mainPlayerInfo.id)
      .eq('user_id', userId)
      .eq('league_id', leagueId)
      .single();

    if (mainPlayerDataError) throw mainPlayerDataError;

    const reservePlayerTable = reservePlayerInfo.isGoalie ? 'getmyteamgoalie' : 'getmyteamplayer';
    const { data: reservePlayerData, error: reservePlayerDataError } = await supabase
      .from(reservePlayerTable)
      .select('salary')
      .eq('playerid', reservePlayerInfo.id)
      .eq('user_id', userId)
      .eq('league_id', leagueId)
      .single();

    if (reservePlayerDataError) throw reservePlayerDataError;

    // 2. Vérifier le plafond salarial du club principal si activé (pour la promotion du joueur réserve)
    if (leagueSettings?.salary_cap && leagueSettings?.max_salary_cap) {
      const { data: currentMainCap, error: mainCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('isnhl', true)
        .single();

      if (mainCapError && mainCapError.code !== 'PGRST116') throw mainCapError;

      const currentMainSalaryCap = currentMainCap?.salary_cap || 0;
      
      // Calculer le nouveau plafond après la permutation
      // On retire le salaire du joueur principal qui descend et on ajoute celui du joueur réserve qui monte
      const newMainSalaryCap = currentMainSalaryCap - mainPlayerData.salary + reservePlayerData.salary;

      // Vérifier si cela dépasserait la limite
      if (newMainSalaryCap > leagueSettings.max_salary_cap) {
        const excess = newMainSalaryCap - leagueSettings.max_salary_cap;
        return {
          success: false,
          error: { message: `Main club salary cap would be exceeded by $${excess.toLocaleString()}` }
        };
      }
    }

    // 3. Vérifier le plafond salarial des réserves si activé
    if (leagueSettings?.salary_cap_reserve && leagueSettings?.max_salary_cap_reserve) {
      const { data: currentReserveCap, error: reserveCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('isnhl', false)
        .single();

      if (reserveCapError && reserveCapError.code !== 'PGRST116') throw reserveCapError;

      const currentReserveSalaryCap = currentReserveCap?.salary_cap || 0;
      
      // Calculer le nouveau plafond après la permutation
      // On retire le salaire du joueur réserve qui monte et on ajoute celui du joueur principal qui descend
      const newReserveSalaryCap = currentReserveSalaryCap - reservePlayerData.salary + mainPlayerData.salary;

      // Vérifier si cela dépasserait la limite
      if (newReserveSalaryCap > leagueSettings.max_salary_cap_reserve) {
        const excess = newReserveSalaryCap - leagueSettings.max_salary_cap_reserve;
        return {
          success: false,
          error: { message: `Reserve salary cap would be exceeded by $${excess.toLocaleString()}` }
        };
      }
    }

    // 4. Vérifier les limites d'effectif par position (seulement pour les joueurs, pas les gardiens)
    if (!mainPlayerInfo.isGoalie && !reservePlayerInfo.isGoalie) {
      // Récupérer les limites d'effectif de la ligue
      const { data: positionLimits, error: positionLimitsError } = await supabase
        .from('league_settings')
        .select('number_foward, number_def')
        .eq('league_id', leagueId)
        .single();

      if (positionLimitsError && positionLimitsError.code !== 'PGRST116') throw positionLimitsError;

      // Récupérer les positions des deux joueurs
      const { data: mainPlayerPosition, error: mainPlayerPositionError } = await supabase
        .from('getmyteamplayer')
        .select('positioncode')
        .eq('playerid', mainPlayerInfo.id)
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .single();

      if (mainPlayerPositionError) throw mainPlayerPositionError;

      const { data: reservePlayerPosition, error: reservePlayerPositionError } = await supabase
        .from('getmyteamplayer')
        .select('positioncode')
        .eq('playerid', reservePlayerInfo.id)
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .single();

      if (reservePlayerPositionError) throw reservePlayerPositionError;

      // Compter les joueurs actuels du club principal par position (excluant les scratch et les blessés LTIR)
      const { data: currentMainPlayers, error: mainPlayersError } = await supabase
        .from('getmyteamplayer')
        .select('playerid, positioncode')
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .eq('isnhl', true)
        .eq('is_scratch', false)
        .eq('is_injured', false);

      if (mainPlayersError) throw mainPlayersError;

      // Compter les joueurs actuels par position
      const currentForwards = currentMainPlayers.filter(p => 
        ['L', 'C', 'R'].includes(p.positioncode)
      ).length;
      
      const currentDefensemen = currentMainPlayers.filter(p => 
        p.positioncode === 'D'
      ).length;

      // Calculer le changement net après la permutation
      let forwardsDelta = 0;
      let defensemenDelta = 0;

      // Le joueur principal qui part vers les réserves (on retire du club principal)
      if (['L', 'C', 'R'].includes(mainPlayerPosition.positioncode)) {
        forwardsDelta -= 1;
      } else if (mainPlayerPosition.positioncode === 'D') {
        defensemenDelta -= 1;
      }

      // Le joueur réserve qui arrive au club principal (on ajoute au club principal)
      if (['L', 'C', 'R'].includes(reservePlayerPosition.positioncode)) {
        forwardsDelta += 1;
      } else if (reservePlayerPosition.positioncode === 'D') {
        defensemenDelta += 1;
      }

      // Vérifier si les nouvelles limites seraient dépassées
      const maxForwards = positionLimits?.number_foward || 12;
      const maxDefensemen = positionLimits?.number_def || 6;

      const newForwardsCount = currentForwards + forwardsDelta;
      const newDefensemenCount = currentDefensemen + defensemenDelta;

      if (newForwardsCount > maxForwards) {
        return {
          success: false,
          error: { message: `⚠️ Permutation impossible : Limite d'attaquants dépassée (${newForwardsCount}/${maxForwards}).\n\nVous avez actuellement ${currentForwards} attaquants dans votre club principal. Cette permutation vous donnerait ${newForwardsCount} attaquants, ce qui dépasse la limite autorisée de ${maxForwards}.` }
        };
      }

      if (newDefensemenCount > maxDefensemen) {
        return {
          success: false,
          error: { message: `⚠️ Permutation impossible : Limite de défenseurs dépassée (${newDefensemenCount}/${maxDefensemen}).\n\nVous avez actuellement ${currentDefensemen} défenseurs dans votre club principal. Cette permutation vous donnerait ${newDefensemenCount} défenseurs, ce qui dépasse la limite autorisée de ${maxDefensemen}.` }
        };
      }
    }
    
    // 4b. Vérifier les limites de la liste de protection si activée
    if (leagueSettings?.protectionlist) {
      const maxReserveProtect = leagueSettings?.number_reserve_protect || 0;
      const maxProtectedGoalies = leagueSettings?.number_goalie_protect || 1;
      const maxProtectedDef = leagueSettings?.number_def_protect || 3;
      const maxProtectedFwd = leagueSettings?.number_foward_protect || 8;

      // Vérifier si l'un ou l'autre des joueurs est dans la liste de protection
      const [{ data: mainProtEntry }, { data: reserveProtEntry }] = await Promise.all([
        supabase
          .from('protection_list')
          .select('playerid')
          .eq('playerid', mainPlayerInfo.id)
          .eq('user_id', userId)
          .eq('league_id', leagueId)
          .maybeSingle(),
        supabase
          .from('protection_list')
          .select('playerid')
          .eq('playerid', reservePlayerInfo.id)
          .eq('user_id', userId)
          .eq('league_id', leagueId)
          .maybeSingle()
      ]);

      const mainPlayerIsProtected = !!mainProtEntry;
      const reservePlayerIsProtected = !!reserveProtEntry;

      if (mainPlayerIsProtected || reservePlayerIsProtected) {
        // Charger tous les IDs protégés une seule fois
        const { data: allProtected } = await supabase
          .from('protection_list')
          .select('playerid')
          .eq('user_id', userId)
          .eq('league_id', leagueId);
        const protectedIds = (allProtected || []).map((p: any) => p.playerid);

        // --- Check 1: mainPlayer (club principal → réserves) ---
        // Si protégé, vérifier que la limite de réservistes protégés ne serait pas dépassée
        if (mainPlayerIsProtected && maxReserveProtect > 0) {
          const { data: reserveProtected } = await supabase
            .from('league_players_user')
            .select('player_id')
            .eq('user_id', userId)
            .eq('league_id', leagueId)
            .eq('isnhl', false)
            .in('player_id', protectedIds);
          const currentReserveProtectedCount = (reserveProtected || []).length;
          // Delta net : +1 car mainPlayer arrive en réserve, -1 si reservePlayer (qui monte) était aussi protégé
          const netReserveProtected = currentReserveProtectedCount + 1 - (reservePlayerIsProtected ? 1 : 0);
          if (netReserveProtected > maxReserveProtect) {
            return {
              success: false,
              error: { message: `Impossible d'effectuer cette permutation — le joueur du club principal est dans votre liste de protection et la limite de réservistes protégés serait dépassée (${netReserveProtected}/${maxReserveProtect}). Retirez-le d'abord de la liste de protection avant d'effectuer ce mouvement.` }
            };
          }
        }

        // --- Check 2: reservePlayer (réserves → club principal) ---
        // Si protégé, vérifier que la limite de protection par position ne serait pas dépassée
        if (reservePlayerIsProtected) {
          if (reservePlayerInfo.isGoalie) {
            const { data: mainProtectedGoalies } = await supabase
              .from('getmyteamgoalie')
              .select('playerid')
              .eq('user_id', userId)
              .eq('league_id', leagueId)
              .eq('isnhl', true)
              .in('playerid', protectedIds);
            const currentProtectedGoalies = (mainProtectedGoalies || []).length;
            // Delta net : +1 gardien arrive, -1 si le gardien principal (qui descend) était aussi protégé
            const netProtectedGoalies = currentProtectedGoalies + 1 - (mainPlayerIsProtected && mainPlayerInfo.isGoalie ? 1 : 0);
            if (netProtectedGoalies > maxProtectedGoalies) {
              return {
                success: false,
                error: { message: `Impossible d'effectuer cette permutation — ce gardien est dans votre liste de protection et la limite de gardiens protégés dans le club principal serait dépassée (${netProtectedGoalies}/${maxProtectedGoalies}). Retirez-le d'abord de la liste de protection avant d'effectuer ce mouvement.` }
              };
            }
          } else {
            // Joueur skateur — récupérer la position du joueur réserve
            const { data: reservePosData } = await supabase
              .from('getmyteamplayer')
              .select('positioncode')
              .eq('playerid', reservePlayerInfo.id)
              .eq('user_id', userId)
              .eq('league_id', leagueId)
              .single();
            const reservePos = reservePosData?.positioncode || '';

            // Récupérer les joueurs protégés dans le club principal avec leur position
            const { data: mainProtectedData } = await supabase
              .from('league_players_user')
              .select('player_id')
              .eq('user_id', userId)
              .eq('league_id', leagueId)
              .eq('isnhl', true)
              .in('player_id', protectedIds);
            const mainProtectedIds = (mainProtectedData || []).map((p: any) => p.player_id);

            const { data: mainProtectedPlayers } = await supabase
              .from('getmyteamplayer')
              .select('playerid, positioncode')
              .eq('user_id', userId)
              .eq('league_id', leagueId)
              .eq('isnhl', true)
              .in('playerid', mainProtectedIds);

            // Position du joueur principal (qui descend) pour calculer le delta net
            let mainPos = '';
            if (mainPlayerIsProtected && !mainPlayerInfo.isGoalie) {
              const { data: mainPosData } = await supabase
                .from('getmyteamplayer')
                .select('positioncode')
                .eq('playerid', mainPlayerInfo.id)
                .eq('user_id', userId)
                .eq('league_id', leagueId)
                .single();
              mainPos = mainPosData?.positioncode || '';
            }

            if (reservePos === 'D') {
              const currentProtectedDef = (mainProtectedPlayers || []).filter((p: any) => p.positioncode === 'D').length;
              const netProtectedDef = currentProtectedDef + 1 - (mainPlayerIsProtected && mainPos === 'D' ? 1 : 0);
              if (netProtectedDef > maxProtectedDef) {
                return {
                  success: false,
                  error: { message: `Impossible d'effectuer cette permutation — ce défenseur est dans votre liste de protection et la limite de défenseurs protégés dans le club principal serait dépassée (${netProtectedDef}/${maxProtectedDef}). Retirez-le d'abord de la liste de protection avant d'effectuer ce mouvement.` }
                };
              }
            } else if (['C', 'L', 'R'].includes(reservePos)) {
              const currentProtectedFwd = (mainProtectedPlayers || []).filter((p: any) => ['C', 'L', 'R'].includes(p.positioncode)).length;
              const netProtectedFwd = currentProtectedFwd + 1 - (mainPlayerIsProtected && ['C', 'L', 'R'].includes(mainPos) ? 1 : 0);
              if (netProtectedFwd > maxProtectedFwd) {
                return {
                  success: false,
                  error: { message: `Impossible d'effectuer cette permutation — cet attaquant est dans votre liste de protection et la limite d'attaquants protégés dans le club principal serait dépassée (${netProtectedFwd}/${maxProtectedFwd}). Retirez-le d'abord de la liste de protection avant d'effectuer ce mouvement.` }
                };
              }
            }
          }
        }
      }
    }

    // 5. Incrémenter le compteur de rétrogradations pour le joueur principal -> réserve
    const { error: updateDemotionError } = await supabase
      .from('leagues_users')
      .update({ number_of_demotion_used: currentDemotions + 1 })
      .eq('league_id', leagueId)
      .eq('user_id', userId);
    
    if (updateDemotionError) throw updateDemotionError;

    // 6. Mettre à jour le premier joueur (Main -> Reserve)
    const { error: mainPlayerError } = await supabase
      .from('league_players_user')
      .update({ isnhl: false })
      .eq('player_id', mainPlayerInfo.id)
      .eq('user_id', userId)
      .eq('league_id', leagueId);

    if (mainPlayerError) throw mainPlayerError;

    // 7. Mettre à jour le deuxième joueur (Reserve -> Main)
    const { error: reservePlayerError } = await supabase
      .from('league_players_user')
      .update({ isnhl: true })
      .eq('player_id', reservePlayerInfo.id)
      .eq('user_id', userId)
      .eq('league_id', leagueId);

    if (reservePlayerError) throw reservePlayerError;

    return { 
      success: true,
      demotionInfo: {
        current: currentDemotions + 1,
        max: maxDemotions
      }
    };
  } catch (error: any) {
    console.error('Error swapping players:', error);
    return { success: false, error: error.message };
  }
}

// Fonction pour permuter entre club principal et scratch players
export async function swapPlayerWithScratch(
  leagueId: number,
  userId: number,
  mainPlayerInfo: { id: number; isGoalie: boolean },
  scratchPlayerInfo: { id: number; isGoalie: boolean }
) {
  try {
    // 1. Récupérer les paramètres de la ligue pour connaître les limites d'effectif
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('number_foward, number_def, number_goalie, nomouvement, nmstarth, nmendh, nmstartd, nmendd, deadline_demotion_promotion')
      .eq('league_id', leagueId)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

    const movementCheck = evaluateNoMovementWindow(leagueSettings);
    if (movementCheck.blocked) {
      return {
        success: false,
        error: { message: movementCheck.message || "Les mouvements d'effectif sont actuellement bloqués." }
      };
    }

    const deadlineCheck = evaluatePromotionDeadline(leagueSettings);
    if (deadlineCheck.blocked) {
      return {
        success: false,
        error: { message: deadlineCheck.message || "Les promotions et rétrogradations ne sont plus autorisées." }
      };
    }

    // 2. Récupérer les informations du joueur scratch pour connaître sa position
    const scratchPlayerTable = scratchPlayerInfo.isGoalie ? 'getmyteamgoalie' : 'getmyteamplayer';
    const { data: scratchPlayerData, error: scratchPlayerDataError } = await supabase
      .from(scratchPlayerTable)
      .select(scratchPlayerInfo.isGoalie ? 'goaliefullname' : 'positioncode, skaterfullname')
      .eq('playerid', scratchPlayerInfo.id)
      .eq('user_id', userId)
      .eq('league_id', leagueId)
      .single();

    if (scratchPlayerDataError) throw scratchPlayerDataError;

    // 3. Vérifier les limites d'effectif seulement si c'est un joueur (pas gardien)
    if (!scratchPlayerInfo.isGoalie) {
      const scratchPosition = (scratchPlayerData as any).positioncode;
      
      // Récupérer la position du joueur principal qui part vers scratch
      const { data: mainPlayerData, error: mainPlayerDataError } = await supabase
        .from('getmyteamplayer')
        .select('positioncode')
        .eq('playerid', mainPlayerInfo.id)
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .single();

      if (mainPlayerDataError) throw mainPlayerDataError;
      const mainPlayerPosition = mainPlayerData.positioncode;
      
      // Compter les joueurs actuels du club principal par position
      const { data: currentMainPlayers, error: mainPlayersError } = await supabase
        .from('getmyteamplayer')
        .select('playerid, positioncode')
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .eq('isnhl', true)
        .eq('is_scratch', false)
        .eq('is_injured', false);

      if (mainPlayersError) throw mainPlayersError;

      // Compter les joueurs actuels par position
      const currentForwards = currentMainPlayers.filter(p => 
        ['L', 'C', 'R'].includes(p.positioncode)
      ).length;
      
      const currentDefensemen = currentMainPlayers.filter(p => 
        p.positioncode === 'D'
      ).length;

      console.log('DEBUG - Current counts:', { currentForwards, currentDefensemen });
      console.log('DEBUG - Main player position:', mainPlayerPosition);
      console.log('DEBUG - Scratch player position:', scratchPosition);

      // Calculer le changement net après la permutation
      let forwardsDelta = 0;
      let defensemenDelta = 0;

      // Le joueur principal qui part vers scratch (on retire du club principal)
      if (['L', 'C', 'R'].includes(mainPlayerPosition)) {
        forwardsDelta -= 1;
        console.log('DEBUG - Removing forward from main club');
      } else if (mainPlayerPosition === 'D') {
        defensemenDelta -= 1;
        console.log('DEBUG - Removing defenseman from main club');
      }

      // Le scratch player qui arrive au club principal (on ajoute au club principal)
      if (['L', 'C', 'R'].includes(scratchPosition)) {
        forwardsDelta += 1;
        console.log('DEBUG - Adding forward to main club');
      } else if (scratchPosition === 'D') {
        defensemenDelta += 1;
        console.log('DEBUG - Adding defenseman to main club');
      }

      console.log('DEBUG - Deltas calculated:', { forwardsDelta, defensemenDelta });

      // Vérifier si les nouvelles limites seraient dépassées
      const maxForwards = leagueSettings?.number_foward || 12;
      const maxDefensemen = leagueSettings?.number_def || 6;

      const newForwardsCount = currentForwards + forwardsDelta;
      const newDefensemenCount = currentDefensemen + defensemenDelta;

      console.log('DEBUG - Final counts would be:', { 
        newForwardsCount, 
        maxForwards, 
        newDefensemenCount, 
        maxDefensemen 
      });

      if (newForwardsCount > maxForwards) {
        console.log('DEBUG - Forward limit would be exceeded!');
        return {
          success: false,
          error: { message: `⚠️ Permutation impossible : Limite d'attaquants dépassée (${newForwardsCount}/${maxForwards}).\n\nVous avez actuellement ${currentForwards} attaquants dans votre club principal. Cette permutation vous donnerait ${newForwardsCount} attaquants, ce qui dépasse la limite autorisée de ${maxForwards}.` }
        };
      }

      if (newDefensemenCount > maxDefensemen) {
        console.log('DEBUG - Defenseman limit would be exceeded!');
        return {
          success: false,
          error: { message: `⚠️ Permutation impossible : Limite de défenseurs dépassée (${newDefensemenCount}/${maxDefensemen}).\n\nVous avez actuellement ${currentDefensemen} défenseurs dans votre club principal. Cette permutation vous donnerait ${newDefensemenCount} défenseurs, ce qui dépasse la limite autorisée de ${maxDefensemen}.` }
        };
      }

      console.log('DEBUG - All position limits are OK, permutation can proceed');
    } else {
      // Pour les gardiens, vérifier la limite
      const { data: currentMainGoalies, error: mainGoaliesError } = await supabase
        .from('getmyteamgoalie')
        .select('playerid')
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .eq('isnhl', true)
        .eq('is_scratch', false)
        .eq('is_injured', false);

      if (mainGoaliesError) throw mainGoaliesError;

      const currentGoaliesCount = currentMainGoalies.filter(g => g.playerid !== mainPlayerInfo.id).length;
      const maxGoalies = leagueSettings?.number_goalie || 2;

      if (currentGoaliesCount >= maxGoalies) {
        return {
          success: false,
          error: { message: `Limite de gardiens atteinte (${maxGoalies}). Impossible d'effectuer la permutation.` }
        };
      }
    }

    // 4. Effectuer la permutation
    // Mettre le joueur principal en scratch
    const { error: mainPlayerError } = await supabase
      .from('league_players_user')
      .update({ is_scratch: true })
      .eq('player_id', mainPlayerInfo.id)
      .eq('user_id', userId)
      .eq('league_id', leagueId);

    if (mainPlayerError) throw mainPlayerError;

    // Mettre le scratch player dans le club principal
    const { error: scratchPlayerError } = await supabase
      .from('league_players_user')
      .update({ is_scratch: false })
      .eq('player_id', scratchPlayerInfo.id)
      .eq('user_id', userId)
      .eq('league_id', leagueId);

    if (scratchPlayerError) throw scratchPlayerError;

    return { success: true };
  } catch (error: any) {
    console.error('Error swapping player with scratch:', error);
    return { success: false, error: error.message };
  }
}

// Fonction pour permuter entre scratch players et réserves
export async function swapScratchWithReserve(
  leagueId: number,
  userId: number,
  scratchPlayerInfo: { id: number; isGoalie: boolean },
  reservePlayerInfo: { id: number; isGoalie: boolean }
) {
  try {
    // 1. Vérifier la limite de rétrogradation car cela compte comme une rétrogradation
    // Récupérer les paramètres de la ligue pour connaître la limite de rétrogradations
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('max_demotion, salary_cap, max_salary_cap, salary_cap_reserve, max_salary_cap_reserve, nomouvement, nmstarth, nmendh, nmstartd, nmendd, deadline_demotion_promotion')
      .eq('league_id', leagueId)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

    const movementCheck = evaluateNoMovementWindow(leagueSettings);
    if (movementCheck.blocked) {
      return {
        success: false,
        error: { message: movementCheck.message || "Les mouvements d'effectif sont actuellement bloqués." }
      };
    }

    const deadlineCheck = evaluatePromotionDeadline(leagueSettings);
    if (deadlineCheck.blocked) {
      return {
        success: false,
        error: { message: deadlineCheck.message || "Les promotions et rétrogradations ne sont plus autorisées." }
      };
    }
    
    const maxDemotions = leagueSettings?.max_demotion || 10; // Valeur par défaut si non définie
    
    // Vérifier combien de rétrogradations ont déjà été utilisées
    const { data: userLeague, error: userLeagueError } = await supabase
      .from('leagues_users')
      .select('number_of_demotion_used')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .single();
    
    if (userLeagueError && userLeagueError.code !== 'PGRST116') throw userLeagueError;
    
    const currentDemotions = userLeague?.number_of_demotion_used || 0;
    
    // Vérifier si la limite serait dépassée
    if (currentDemotions >= maxDemotions) {
      return {
        success: false,
        error: { message: `⚠️ Permutation impossible : Limite de rétrogradations atteinte.\n\nRétrogradations utilisées : ${currentDemotions}/${maxDemotions}` }
      };
    }

    // 2. Récupérer les salaires des deux joueurs
    const scratchPlayerTable = scratchPlayerInfo.isGoalie ? 'getmyteamgoalie' : 'getmyteamplayer';
    const { data: scratchPlayerData, error: scratchPlayerDataError } = await supabase
      .from(scratchPlayerTable)
      .select('salary')
      .eq('playerid', scratchPlayerInfo.id)
      .eq('user_id', userId)
      .eq('league_id', leagueId)
      .single();

    if (scratchPlayerDataError) throw scratchPlayerDataError;

    const reservePlayerTable = reservePlayerInfo.isGoalie ? 'getmyteamgoalie' : 'getmyteamplayer';
    const { data: reservePlayerData, error: reservePlayerDataError } = await supabase
      .from(reservePlayerTable)
      .select('salary')
      .eq('playerid', reservePlayerInfo.id)
      .eq('user_id', userId)
      .eq('league_id', leagueId)
      .single();

    if (reservePlayerDataError) throw reservePlayerDataError;

    const scratchPlayerSalary = scratchPlayerData.salary || 0;
    const reservePlayerSalary = reservePlayerData.salary || 0;

    // 3. Vérifier le plafond salarial du club principal si activé (scratch player vers réserve = libération de salaire principal)
    if (leagueSettings?.salary_cap && leagueSettings?.max_salary_cap) {
      // Le scratch player libère son salaire du club principal quand il va en réserve
      // Pas de validation nécessaire car on libère de l'espace salarial principal
    }

    // 4. Vérifier le plafond salarial des réserves si activé
    if (leagueSettings?.salary_cap_reserve && leagueSettings?.max_salary_cap_reserve) {
      // Calculer le plafond salarial actuel des réserves
      const { data: currentReserveSalaryCap, error: reserveSalaryCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('isnhl', false)
        .single();

      if (reserveSalaryCapError && reserveSalaryCapError.code !== 'PGRST116') throw reserveSalaryCapError;

      const currentReserveSalary = currentReserveSalaryCap?.salary_cap || 0;
      
      // Calculer le nouveau plafond après la permutation
      // Le scratch player ajoute son salaire aux réserves
      // Le joueur réserve libère son salaire des réserves
      const newReserveSalary = currentReserveSalary + scratchPlayerSalary - reservePlayerSalary;
      
      if (newReserveSalary > leagueSettings.max_salary_cap_reserve) {
        return {
          success: false,
          error: { message: `⚠️ Permutation impossible : Plafond salarial des réserves dépassé.\n\nSalaire actuel des réserves : ${currentReserveSalary.toLocaleString()}$\nSalaire du scratch player : ${scratchPlayerSalary.toLocaleString()}$\nSalaire du joueur réserve : ${reservePlayerSalary.toLocaleString()}$\nNouveau total : ${newReserveSalary.toLocaleString()}$\nLimite autorisée : ${leagueSettings.max_salary_cap_reserve.toLocaleString()}$` }
        };
      }
    }

    // 5. Effectuer la permutation
    // Mettre le scratch player en réserve (is_scratch = false, isnhl = false)
    const { error: scratchPlayerError } = await supabase
      .from('league_players_user')
      .update({ 
        is_scratch: false,
        isnhl: false
      })
      .eq('player_id', scratchPlayerInfo.id)
      .eq('user_id', userId)
      .eq('league_id', leagueId);

    if (scratchPlayerError) throw scratchPlayerError;

    // Mettre le joueur réserve en scratch (is_scratch = true, isnhl = true)
    const { error: reservePlayerError } = await supabase
      .from('league_players_user')
      .update({ 
        is_scratch: true,
        isnhl: true
      })
      .eq('player_id', reservePlayerInfo.id)
      .eq('user_id', userId)
      .eq('league_id', leagueId);

    if (reservePlayerError) throw reservePlayerError;

    // 6. Incrémenter le compteur de rétrogradations car cette permutation compte comme une rétrogradation
    const { error: updateDemotionError } = await supabase
      .from('leagues_users')
      .update({ number_of_demotion_used: currentDemotions + 1 })
      .eq('league_id', leagueId)
      .eq('user_id', userId);
    
    if (updateDemotionError) throw updateDemotionError;

    return { 
      success: true,
      demotionInfo: {
        current: currentDemotions + 1,
        max: maxDemotions
      }
    };
  } catch (error: any) {
    console.error('Error swapping scratch with reserve:', error);
    return { success: false, error: error.message };
  }
}

// Fonction pour échanger 4 joueurs simultanément (2 du club principal avec 2 des réserves)
export async function swapFourPlayers(
  leagueId: number,
  userId: number,
  mainPlayers: { id: number; isGoalie: boolean }[],
  reservePlayers: { id: number; isGoalie: boolean }[]
) {
  try {
    // Vérifier qu'on a exactement 2 joueurs de chaque côté
    if (mainPlayers.length !== 2 || reservePlayers.length !== 2) {
      return {
        success: false,
        error: { message: "Vous devez sélectionner exactement 2 joueurs du club principal et 2 joueurs des réserves." }
      };
    }

    // 1. Récupérer les paramètres de la ligue
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('max_demotion, salary_cap, max_salary_cap, salary_cap_reserve, max_salary_cap_reserve, nomouvement, nmstarth, nmendh, nmstartd, nmendd, deadline_demotion_promotion, number_foward, number_def, number_goalie')
      .eq('league_id', leagueId)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

    // Vérifier la fenêtre de mouvement
    const movementCheck = evaluateNoMovementWindow(leagueSettings);
    if (movementCheck.blocked) {
      return {
        success: false,
        error: { message: movementCheck.message || "Les mouvements d'effectif sont actuellement bloqués." }
      };
    }

    const deadlineCheck = evaluatePromotionDeadline(leagueSettings);
    if (deadlineCheck.blocked) {
      return {
        success: false,
        error: { message: deadlineCheck.message || "Les promotions et rétrogradations ne sont plus autorisées." }
      };
    }

    // 2. Vérifier les limites de rétrogradation (2 rétrogradations car 2 joueurs descendent)
    const maxDemotions = leagueSettings?.max_demotion || 10;
    const { data: userLeague, error: userLeagueError } = await supabase
      .from('leagues_users')
      .select('number_of_demotion_used')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .single();
    
    if (userLeagueError && userLeagueError.code !== 'PGRST116') throw userLeagueError;
    
    const currentDemotions = userLeague?.number_of_demotion_used || 0;
    
    // Vérifier si on peut faire 2 rétrogradations
    if (currentDemotions + 2 > maxDemotions) {
      return { 
        success: false, 
        error: { message: `Limite de rétrogradations atteinte. Vous avez utilisé ${currentDemotions}/${maxDemotions} rétrogradations. Cet échange nécessite 2 rétrogradations supplémentaires.` },
        demotionLimit: {
          current: currentDemotions,
          max: maxDemotions,
          needed: 2
        }
      };
    }

    // 3. Récupérer les salaires de tous les joueurs
    const allPlayerData = [];
    
    for (const player of [...mainPlayers, ...reservePlayers]) {
      const playerTable = player.isGoalie ? 'getmyteamgoalie' : 'getmyteamplayer';
      const { data: playerData, error: playerDataError } = await supabase
        .from(playerTable)
        .select('salary, positioncode')
        .eq('playerid', player.id)
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .single();

      if (playerDataError) throw playerDataError;
      allPlayerData.push({ ...player, ...playerData });
    }

    const mainPlayersData = allPlayerData.slice(0, 2);
    const reservePlayersData = allPlayerData.slice(2, 4);

    // 4. Vérifier les plafonds salariaux si activés
    if (leagueSettings?.salary_cap && leagueSettings?.max_salary_cap) {
      // Plafond du club principal
      const { data: currentMainCap, error: mainCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('isnhl', true)
        .single();

      if (mainCapError && mainCapError.code !== 'PGRST116') throw mainCapError;

      const currentMainSalaryCap = currentMainCap?.salary_cap || 0;
      
      // Calculer le nouveau plafond après la permutation
      const mainSalariesOut = mainPlayersData.reduce((sum, p) => sum + (p.salary || 0), 0);
      const reserveSalariesIn = reservePlayersData.reduce((sum, p) => sum + (p.salary || 0), 0);
      const newMainSalaryCap = currentMainSalaryCap - mainSalariesOut + reserveSalariesIn;

      if (newMainSalaryCap > leagueSettings.max_salary_cap) {
        return {
          success: false,
          error: { message: `⚠️ Permutation impossible : Plafond salarial du club principal dépassé.\n\nPlafond actuel : ${currentMainSalaryCap.toLocaleString()}$\nSalaires qui sortent : -${mainSalariesOut.toLocaleString()}$\nSalaires qui arrivent : +${reserveSalariesIn.toLocaleString()}$\nNouveau total : ${newMainSalaryCap.toLocaleString()}$\nLimite autorisée : ${leagueSettings.max_salary_cap.toLocaleString()}$` }
        };
      }
    }

    if (leagueSettings?.salary_cap_reserve && leagueSettings?.max_salary_cap_reserve) {
      // Plafond des réserves
      const { data: currentReserveCap, error: reserveCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('isnhl', false)
        .single();

      if (reserveCapError && reserveCapError.code !== 'PGRST116') throw reserveCapError;

      const currentReserveSalaryCap = currentReserveCap?.salary_cap || 0;
      
      // Calculer le nouveau plafond après la permutation
      const reserveSalariesOut = reservePlayersData.reduce((sum, p) => sum + (p.salary || 0), 0);
      const mainSalariesIn = mainPlayersData.reduce((sum, p) => sum + (p.salary || 0), 0);
      const newReserveSalaryCap = currentReserveSalaryCap - reserveSalariesOut + mainSalariesIn;

      if (newReserveSalaryCap > leagueSettings.max_salary_cap_reserve) {
        return {
          success: false,
          error: { message: `⚠️ Permutation impossible : Plafond salarial des réserves dépassé.\n\nPlafond actuel : ${currentReserveSalaryCap.toLocaleString()}$\nSalaires qui sortent : -${reserveSalariesOut.toLocaleString()}$\nSalaires qui arrivent : +${mainSalariesIn.toLocaleString()}$\nNouveau total : ${newReserveSalaryCap.toLocaleString()}$\nLimite autorisée : ${leagueSettings.max_salary_cap_reserve.toLocaleString()}$` }
        };
      }
    }

    // 5. Vérifier les limites de positions pour les joueurs non-gardiens
    const nonGoalieMainPlayers = mainPlayersData.filter(p => !p.isGoalie);
    const nonGoalieReservePlayers = reservePlayersData.filter(p => !p.isGoalie);

    if (nonGoalieMainPlayers.length > 0 || nonGoalieReservePlayers.length > 0) {
      // Récupérer les joueurs actuels du club principal
      const { data: currentMainPlayers, error: mainPlayersError } = await supabase
        .from('getmyteamplayer')
        .select('playerid, positioncode')
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .eq('isnhl', true)
        .eq('is_scratch', false)
        .eq('is_injured', false);

      if (mainPlayersError) throw mainPlayersError;

      // Compter les positions actuelles
      const currentForwards = currentMainPlayers.filter(p => ['L', 'C', 'R'].includes(p.positioncode)).length;
      const currentDefensemen = currentMainPlayers.filter(p => p.positioncode === 'D').length;

      // Calculer les changements
      let forwardsDelta = 0;
      let defensemenDelta = 0;

      // Joueurs qui sortent du club principal
      nonGoalieMainPlayers.forEach(player => {
        if (['L', 'C', 'R'].includes(player.positioncode)) {
          forwardsDelta -= 1;
        } else if (player.positioncode === 'D') {
          defensemenDelta -= 1;
        }
      });

      // Joueurs qui arrivent au club principal
      nonGoalieReservePlayers.forEach(player => {
        if (['L', 'C', 'R'].includes(player.positioncode)) {
          forwardsDelta += 1;
        } else if (player.positioncode === 'D') {
          defensemenDelta += 1;
        }
      });

      const newForwardsCount = currentForwards + forwardsDelta;
      const newDefensemenCount = currentDefensemen + defensemenDelta;
      const maxForwards = leagueSettings?.number_foward || 12;
      const maxDefensemen = leagueSettings?.number_def || 6;

      if (newForwardsCount > maxForwards) {
        return {
          success: false,
          error: { message: `⚠️ Permutation impossible : Limite d'attaquants dépassée (${newForwardsCount}/${maxForwards}).\n\nVous avez actuellement ${currentForwards} attaquants dans votre club principal. Cette permutation vous donnerait ${newForwardsCount} attaquants, ce qui dépasse la limite autorisée de ${maxForwards}.` }
        };
      }

      if (newDefensemenCount > maxDefensemen) {
        return {
          success: false,
          error: { message: `⚠️ Permutation impossible : Limite de défenseurs dépassée (${newDefensemenCount}/${maxDefensemen}).\n\nVous avez actuellement ${currentDefensemen} défenseurs dans votre club principal. Cette permutation vous donnerait ${newDefensemenCount} défenseurs, ce qui dépasse la limite autorisée de ${maxDefensemen}.` }
        };
      }
    }

    // 6. Vérifier les limites de gardiens
    const goalieMainPlayers = mainPlayersData.filter(p => p.isGoalie);
    const goalieReservePlayers = reservePlayersData.filter(p => p.isGoalie);

    if (goalieMainPlayers.length > 0 || goalieReservePlayers.length > 0) {
      const { data: currentMainGoalies, error: mainGoaliesError } = await supabase
        .from('getmyteamgoalie')
        .select('playerid')
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .eq('isnhl', true)
        .eq('is_scratch', false)
        .eq('is_injured', false);

      if (mainGoaliesError) throw mainGoaliesError;

      const currentGoaliesCount = currentMainGoalies.length;
      const goaliesDelta = goalieReservePlayers.length - goalieMainPlayers.length;
      const newGoaliesCount = currentGoaliesCount + goaliesDelta;
      const maxGoalies = leagueSettings?.number_goalie || 2;

      if (newGoaliesCount > maxGoalies) {
        return {
          success: false,
          error: { message: `⚠️ Permutation impossible : Limite de gardiens dépassée (${newGoaliesCount}/${maxGoalies}).\n\nVous avez actuellement ${currentGoaliesCount} gardiens dans votre club principal. Cette permutation vous donnerait ${newGoaliesCount} gardiens, ce qui dépasse la limite autorisée de ${maxGoalies}.` }
        };
      }
    }

    // 7. Effectuer la permutation
    // Déplacer les joueurs du club principal vers les réserves
    for (const player of mainPlayers) {
      const { error: mainPlayerError } = await supabase
        .from('league_players_user')
        .update({ isnhl: false })
        .eq('player_id', player.id)
        .eq('user_id', userId)
        .eq('league_id', leagueId);

      if (mainPlayerError) throw mainPlayerError;
    }

    // Déplacer les joueurs des réserves vers le club principal
    for (const player of reservePlayers) {
      const { error: reservePlayerError } = await supabase
        .from('league_players_user')
        .update({ isnhl: true })
        .eq('player_id', player.id)
        .eq('user_id', userId)
        .eq('league_id', leagueId);

      if (reservePlayerError) throw reservePlayerError;
    }

    // 8. Incrémenter le compteur de rétrogradations (+2 car 2 joueurs descendent)
    const { error: updateDemotionError } = await supabase
      .from('leagues_users')
      .update({ number_of_demotion_used: currentDemotions + 2 })
      .eq('league_id', leagueId)
      .eq('user_id', userId);
    
    if (updateDemotionError) throw updateDemotionError;

    return { 
      success: true,
      demotionInfo: {
        current: currentDemotions + 2,
        max: maxDemotions
      }
    };
  } catch (error: any) {
    console.error('Error swapping four players:', error);
    return { success: false, error: error.message };
  }
}

//** MyTeam section end **

//** Trade section start **

export async function getUserByLeaguSaufUserConnecter(
  leagueId: number,
  CurrentUserID: number,
  onlyBuildComplete = false
) {
  try {
    let query = supabase
      .from('getallteam')
      .select('*')
      .eq('league_id', leagueId)
      .neq('users_user_id', CurrentUserID);

    // Filtrer côté serveur pour rester sous la limite max-rows de PostgREST
    // dans les grosses ligues standard_flexible
    if (onlyBuildComplete) {
      query = query.eq('initial_build_complete', true);
    }

    const { data, error } = await query;

    console.log({ data }, { leagueId }, { CurrentUserID });
    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('Supabase error:', error);
    return { data: null, error };
  }
}

// Met à jour l'abréviation NHL choisie comme logo d'équipe pour un utilisateur dans une ligue
export async function updateTeamNhlLogo(
  leagueId: number,
  userId: number,
  abbrev: string | null
): Promise<{ success: boolean; error?: any }> {
  try {
    const { error } = await supabase
      .from('leagues_users')
      .update({ nhl_team_abbrev: abbrev })
      .eq('league_id', leagueId)
      .eq('user_id', userId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error updating team NHL logo:', error);
    return { success: false, error };
  }
}

// Fonction pour proposer un échange avec notification email
export async function proposeTradeWithNotification(
  leagueId: number,
  tradeLine: number,
  tradeSenderUserId: number,
  tradeSenderPlayerId: string,
  tradeSenderPickId: number | null,
  tradeReceiverUserId: number,
  tradeReceiverPlayerId: string,
  tradeReceiverPickId: number | null,
  isAccepted: boolean,
  isRefused: boolean,
  firstTradeId?: number,
  tradeSenderSalaryRetainPct: number = 0,
  tradeReceiverSalaryRetainPct: number = 0
) {
  try {
    // Proposer le trade d'abord
    const tradeResult = await proposeTrade(
      leagueId,
      tradeLine,
      tradeSenderUserId,
      tradeSenderPlayerId,
      tradeSenderPickId,
      tradeReceiverUserId,
      tradeReceiverPlayerId,
      tradeReceiverPickId,
      isAccepted,
      isRefused,
      firstTradeId,
      tradeSenderSalaryRetainPct,
      tradeReceiverSalaryRetainPct
    );

    if (tradeResult.error) {
      return tradeResult;
    }

    // Si c'est le premier trade de la transaction (tradeLine === 0), envoyer la notification push
    if (tradeLine === 0 && !isAccepted && !isRefused && (tradeSenderPlayerId || tradeSenderPickId) && (tradeReceiverPlayerId || tradeReceiverPickId)) {
      try {
        // Obtenir les informations nécessaires pour la notification push
        const [senderInfo, leagueInfo] = await Promise.all([
          getUserEmail(tradeSenderUserId),
          getLeagueInfo(leagueId)
        ]);

        if (senderInfo.username) {
          // Envoyer une notification push
          try {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
            
            console.log('🔔 Tentative envoi push notification...');
            console.log('📍 URL:', supabaseUrl);
            console.log('👤 Receiver User ID:', tradeReceiverUserId);
            console.log('📧 Sender:', senderInfo.username);
            
            if (!supabaseUrl || !supabaseAnonKey) {
              console.warn('⚠️ Variables d\'environnement manquantes pour push notification');
              return;
            }
            
            const pushPayload = {
              user_id: tradeReceiverUserId,
              title: 'Nouvelle Offre d\'Échange',
              body: `${senderInfo.username} vous propose un échange dans ${leagueInfo.name}`,
              icon: '/image2.png',
              badge: '/image2.png',
              data: {
                type: 'trade',
                leagueId,
                url: '/messages'
              },
              tag: `trade-${Date.now()}`
            };
            
            console.log('📦 Payload:', JSON.stringify(pushPayload, null, 2));
            
            const response = await fetch(`${supabaseUrl}/functions/v1/send-push-notification`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${supabaseAnonKey}`
              },
              body: JSON.stringify(pushPayload)
            });

            console.log('📡 Réponse status:', response.status);
            
            if (response.ok) {
              const result = await response.json();
              console.log('✅ Push notification envoyée avec succès:', result);
            } else {
              const errorText = await response.text();
              console.error('❌ Échec envoi push notification:', response.status, errorText);
            }
          } catch (pushError) {
            console.error('❌ Erreur push notification:', pushError);
            // On ne fait pas échouer le trade si la notification push échoue
          }
        }
      } catch (pushSetupError) {
        console.warn('Error setting up push notification:', pushSetupError);
        // On ne fait pas échouer le trade si la notification push échoue
      }
    }

    return tradeResult;
  } catch (error: any) {
    console.error('Error in proposeTradeWithNotification:', error);
    return { data: null, error };
  }
}

// Fonction pour proposer un échange
export async function proposeTrade(
  leagueId: number,
  tradeLine: number,
  tradeSenderUserId: number,
  tradeSenderPlayerId: string,
  tradeSenderPickId: number | null,
  tradeReceiverUserId: number,
  tradeReceiverPlayerId: string,
  tradeReceiverPickId: number | null,
  isAccepted: boolean,
  isRefused: boolean,
  firstTradeId?: number,
  tradeSenderSalaryRetainPct: number = 0,
  tradeReceiverSalaryRetainPct: number = 0
) {
  try {
    const { data, error } = await supabase.rpc('proposetrade', {
      p_leagueid: leagueId,
      p_trade_line: tradeLine,
      p_trade_sender_id: tradeSenderUserId,
      p_trade_sender_asset: tradeSenderPlayerId ? parseInt(tradeSenderPlayerId) : null,
      p_trade_sender_pick_id: tradeSenderPickId,
      p_trade_receiver_id: tradeReceiverUserId,
      p_trade_receiver_asset: tradeReceiverPlayerId ? parseInt(tradeReceiverPlayerId) : null,
      p_trade_receiver_pick_id: tradeReceiverPickId,
      p_is_accepted: isAccepted,
      p_is_refused: isRefused,
      p_id_trades: tradeLine === 0 ? 0 : firstTradeId,
      p_trade_sender_salary_retain_pct: tradeSenderSalaryRetainPct,
      p_trade_receiver_salary_retain_pct: tradeReceiverSalaryRetainPct,
    });

    if (error) throw error;

    console.log('Trade proposed successfully:', data);
    return { data, error: null };
  } catch (error: any) {
    console.error('Error proposing trade:', error);
    return { data: null, error };
  }
}

// Fonction pour mettre à jour le trades_id
export async function updateTradesId(firstTradeId: number) {
  try {
    const { data, error } = await supabase
      .from('trades_history')
      .update({ trades_id: firstTradeId })
      .eq('th_id', firstTradeId);

    if (error) throw error;
    console.log('Updated trades_id successfully:', data);
    return { data, error: null };
  } catch (error: any) {
    console.error('Error updating trades_id:', error);
    return { data: null, error };
  }
}

// Fonction pour envoyer l'email de notification complet pour un trade
export async function sendCompleteTradeNotification(
  leagueId: number,
  tradesId: number,
  receiverUserId: number
) {
  try {
    // Obtenir tous les trades liés à cette transaction
    const { data: trades, error: tradesError } = await supabase
      .from('gettradeshistory_players_and_users')
      .select('*')
      .eq('leagueid', leagueId)
      .or(`th_id.eq.${tradesId},trades_id.eq.${tradesId}`);

    if (tradesError || !trades || trades.length === 0) {
      console.error('Error fetching trade details:', tradesError);
      return { success: false, error: 'Could not fetch trade details' };
    }

    const firstTrade = trades[0];

    // Obtenir les informations utilisateur et ligue
    const [receiverInfo, senderInfo, leagueInfo] = await Promise.all([
      getUserEmail(receiverUserId),
      getUserEmail(firstTrade.sender_user_id),
      getLeagueInfo(leagueId)
    ]);

    if (!receiverInfo.email || !senderInfo.username) {
      console.error('Missing user information for email notification');
      return { success: false, error: 'Missing user information' };
    }

    // Extraire les détails des assets du trade
    const tradeAssets = extractTradeAssets(trades);

    const emailData = {
      receiverEmail: receiverInfo.email,
      receiverUsername: receiverInfo.username,
      senderUsername: senderInfo.username,
      tradeDetails: tradeAssets,
      leagueName: leagueInfo.name
    };

    const emailResult = await sendTradeNotificationEmail(emailData);
    return emailResult;
  } catch (error: any) {
    console.error('Error sending complete trade notification:', error);
    return { success: false, error: error.message };
  }
}

// Fonction pour obtenir le nom d'un joueur par son ID
// Fonction publique pour obtenir le nom d'un joueur
export async function getPlayerName(playerId: string): Promise<string | null> {
  try {
    // Essayer d'abord dans la table des joueurs
    const { data: playerData, error: playerError } = await supabase
      .from('players')
      .select('skaterfullname')
      .eq('playerid', playerId)
      .order('playerid', { ascending: true })
      .limit(1);

    if (!playerError && playerData && playerData.length > 0 && playerData[0]?.skaterfullname) {
      return playerData[0].skaterfullname;
    }

    // Essayer dans la table des gardiens
    const { data: goalieData, error: goalieError } = await supabase
      .from('goalies')
      .select('goaliefullname')
      .eq('playerid', playerId)
      .order('playerid', { ascending: true })
      .limit(1);

    if (!goalieError && goalieData && goalieData.length > 0 && goalieData[0]?.goaliefullname) {
      return goalieData[0].goaliefullname;
    }

    return null;
  } catch (error) {
    console.error('Error fetching player name:', error);
    return null;
  }
}

// Fonction publique pour obtenir le nom d'un draft pick par son ID
export async function getDraftPickName(pickId: number): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('draftpicks_user_league')
      .select(`
        draftpickstext!draftpicks_id(description_fr)
      `)
      .eq('id', pickId)
      .order('id', { ascending: true })
      .limit(1);

    if (error && error.code !== 'PGRST116') throw error;

    if (data && data.length > 0) {
      // La jointure peut retourner un tableau même pour un seul enregistrement
      const pickText = Array.isArray(data[0]?.draftpickstext) ? data[0].draftpickstext[0] : data[0]?.draftpickstext;
      return pickText?.description_fr || null;
    }

    return null;
  } catch (error: any) {
    console.error('Error getting draft pick name:', error);
    return null;
  }
}

// Fonction pour récupérer les noms des assets d'un trade
export async function getAssetsNames(assets: {players?: number[], goalies?: number[], picks?: number[]}) {
  const playerNames: { [key: number]: string } = {};
  const pickDetails: { [key: number]: { round: number; year: number; description?: string } } = {};

  try {
    // Récupérer les noms des joueurs
    if (assets.players && assets.players.length > 0) {
      for (const playerId of assets.players) {
        const name = await getPlayerName(playerId.toString());
        if (name) playerNames[playerId] = name;
      }
    }

    // Récupérer les noms des gardiens
    if (assets.goalies && assets.goalies.length > 0) {
      for (const goalieId of assets.goalies) {
        const name = await getPlayerName(goalieId.toString());
        if (name) playerNames[goalieId] = name;
      }
    }

    // Récupérer les détails des picks
    if (assets.picks && assets.picks.length > 0) {
      for (const pickId of assets.picks) {
        const description = await getDraftPickName(pickId);
        if (description) {
          pickDetails[pickId] = {
            round: 1, // Valeurs par défaut
            year: 2024,
            description
          };
        }
      }
    }

    return { playerNames, pickDetails };
  } catch (error) {
    console.error('Error fetching assets names:', error);
    return { playerNames: {}, pickDetails: {} };
  }
}

// Fonction pour déplacer un joueur vers la section scratch
export async function movePlayerToScratch(
  playerId: number,
  userId: number,
  leagueId: number
) {
  try {
    const movementCheck = await ensureNoMovementAllowed(leagueId);
    if (movementCheck.blocked) {
      return {
        success: false,
        error: { message: movementCheck.message || "Les mouvements d'effectif sont actuellement bloqués." }
      };
    }

    // Mettre à jour le champ is_scratch à true
    const { data, error } = await supabase
      .from('league_players_user')
      .update({ is_scratch: true })
      .eq('player_id', playerId)
      .eq('user_id', userId)
      .eq('league_id', leagueId);

    if (error) throw error;
    return { success: true, data };
  } catch (error: any) {
    console.error('Error moving player to scratch:', error);
    return { success: false, error };
  }
}

// Fonction pour retirer un joueur de la section scratch
export async function removePlayerFromScratch(
  playerId: number,
  userId: number,
  leagueId: number
) {
  try {
    const movementCheck = await ensureNoMovementAllowed(leagueId);
    if (movementCheck.blocked) {
      return {
        success: false,
        error: { message: movementCheck.message || "Les mouvements d'effectif sont actuellement bloqués." }
      };
    }

    // Mettre à jour le champ is_scratch à false
    const { data, error } = await supabase
      .from('league_players_user')
      .update({ is_scratch: false })
      .eq('player_id', playerId)
      .eq('user_id', userId)
      .eq('league_id', leagueId);

    if (error) throw error;
    return { success: true, data };
  } catch (error: any) {
    console.error('Error removing player from scratch:', error);
    return { success: false, error };
  }
}

// Fonction pour promouvoir un joueur des réserves avec choix de destination
export async function promotePlayerFromReserves(
  playerId: number,
  userId: number,
  leagueId: number,
  destination: 'main_club' | 'scratch',
  isGoalie: boolean = false
) {
  console.log('=== DEBUG promotePlayerFromReserves START ===');
  console.log('Parameters received:', {
    playerId,
    userId,
    leagueId,
    destination,
    isGoalie
  });

  try {
    console.log('Fetching league settings...');
    // Récupérer les paramètres de la ligue pour les vérifications
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('salary_cap, max_salary_cap, number_foward, number_def, number_goalie, number_reserve, number_scratch, protectionlist, number_foward_protect, number_def_protect, number_goalie_protect, nomouvement, nmstarth, nmendh, nmstartd, nmendd, deadline_demotion_promotion')
      .eq('league_id', leagueId)
      .single();

    console.log('League settings result:', { leagueSettings, settingsError });

    if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

    const movementCheck = evaluateNoMovementWindow(leagueSettings);
    if (movementCheck.blocked) {
      console.log('Movement blocked by no-movement window');
      return {
        success: false,
        error: { message: movementCheck.message || "Les mouvements d'effectif sont actuellement bloqués." }
      };
    }

    const deadlineCheck = evaluatePromotionDeadline(leagueSettings);
    if (deadlineCheck.blocked) {
      return {
        success: false,
        error: { message: deadlineCheck.message || "Les promotions et rétrogradations ne sont plus autorisées." }
      };
    }

    // Déterminer le salaire du joueur
    let playerSalary = 0;
    
    console.log('Determining player salary, isGoalie:', isGoalie);
    
    if (isGoalie) {
      console.log('Looking for goalie data...');
      // Chercher dans les gardiens
      const { data: goalieData } = await supabase
        .from('getmyteamgoalie')
        .select('salary')
        .eq('playerid', playerId)
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .single();

      console.log('Goalie data result:', goalieData);

      if (goalieData) {
        playerSalary = goalieData.salary;
        console.log('Player salary set to:', playerSalary);
      } else {
        console.log('ERROR: Goalie not found in team');
        throw new Error('Goalie not found in team');
      }
    } else {
      console.log('Looking for player data...');
      // Chercher dans les joueurs
      const { data: playerData } = await supabase
        .from('getmyteamplayer')
        .select('salary, positioncode')
        .eq('playerid', playerId)
        .eq('user_id', userId)
        .eq('league_id', leagueId)
        .single();

      console.log('Player data result:', playerData);

      if (playerData) {
        playerSalary = playerData.salary;
        console.log('Player salary set to:', playerSalary);
      } else {
        console.log('ERROR: Player not found in team');
        throw new Error('Player not found in team');
      }
    }

    console.log('Checking main club salary cap...');
    // Vérifier le plafond salarial du club principal dans tous les cas (même pour scratch)
    if (leagueSettings?.salary_cap && leagueSettings?.max_salary_cap) {
      console.log('Salary cap is enabled, checking current cap...');
      const { data: currentMainCap, error: mainCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('isnhl', true)
        .single();

      console.log('Current main cap result:', { currentMainCap, mainCapError });

      if (mainCapError && mainCapError.code !== 'PGRST116') throw mainCapError;

      const currentMainSalaryCap = currentMainCap?.salary_cap || 0;
      const newMainSalaryCap = currentMainSalaryCap + playerSalary;

      console.log('Salary cap check:', {
        currentMainSalaryCap,
        playerSalary,
        newMainSalaryCap,
        maxSalaryCap: leagueSettings.max_salary_cap
      });

      if (newMainSalaryCap > leagueSettings.max_salary_cap) {
        const excess = newMainSalaryCap - leagueSettings.max_salary_cap;
        console.log('Salary cap would be exceeded by:', excess);
        return {
          success: false,
          error: { message: `Main club salary cap would be exceeded by $${excess.toLocaleString()}` }
        };
      }
    } else {
      console.log('Salary cap is disabled or not configured');
    }

    console.log('Checking destination:', destination);
    if (destination === 'scratch') {
      console.log('Processing scratch destination...');
      // Validation pour envoyer en scratch
      
      // Vérifier la limite de joueurs de réserve maximum permis par la ligue pour les scratch
      const maxScratchPlayers = leagueSettings?.number_scratch;
      console.log('Max scratch/reserve players allowed:', maxScratchPlayers);
      
      if (maxScratchPlayers && maxScratchPlayers > 0) {
        console.log('Checking current scratch count...');
        // Compter les joueurs actuels en scratch (incluant tous types)
        const { data: currentScratchData, error: scratchCountError } = await supabase
          .from('league_players_user')
          .select('player_id')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .eq('is_scratch', true);

        console.log('Current scratch data result:', { currentScratchData, scratchCountError });

        if (scratchCountError && scratchCountError.code !== 'PGRST116') throw scratchCountError;

        const currentScratchCount = currentScratchData?.length || 0;
        console.log('Current scratch count vs max:', { currentScratchCount, maxScratchPlayers });
        
        if (currentScratchCount >= maxScratchPlayers) {
          console.log('Scratch limit would be exceeded');
          return {
            success: false,
            error: { message: `Limite de scratch players atteinte (${currentScratchCount}/${maxScratchPlayers})` }
          };
        }
      } else {
        console.log('No scratch limit configured');
      }

      console.log('Updating player to scratch status...');
      // Mettre à jour pour envoyer en scratch
      const { data, error } = await supabase
        .from('league_players_user')
        .update({ 
          isnhl: true,
          is_scratch: true
        })
        .eq('player_id', playerId)
        .eq('user_id', userId)
        .eq('league_id', leagueId);

      console.log('Update result for scratch:', { data, error });

      if (error) {
        console.log('ERROR updating to scratch:', error);
        throw error;
      }
      
      console.log('Successfully promoted to scratch');
      return { success: true, data, destination: 'scratch' };

    } else {
      console.log('Processing main_club destination...');
      // Validation pour envoyer au club principal (logique existante)
      
      // Compter les joueurs actuels dans l'équipe principale par position
      if (isGoalie) {
        console.log('Checking goalie limits for main club...');
        // Utiliser la vue get_number_type_goalie_per_team qui exclut déjà les gardiens scratch
        const { data: goalieRosterCounts, error: goalieRosterError } = await supabase
          .from('get_number_type_goalie_per_team')
          .select('number_of_goalie')
          .eq('user_id', userId)
          .eq('league_id', leagueId)
          .single();

        console.log('Current goalie roster counts from view:', { goalieRosterCounts, goalieRosterError });

        if (goalieRosterError && goalieRosterError.code !== 'PGRST116') throw goalieRosterError;

        const currentGoaliesCount = goalieRosterCounts?.number_of_goalie || 0;
        const maxGoalies = leagueSettings?.number_goalie || 2;
        
        console.log('Goalie count check (excluding scratch):', { currentGoaliesCount, maxGoalies });
        
        if (currentGoaliesCount + 1 > maxGoalies) {
          console.log('Goalie limit would be exceeded');
          return {
            success: false,
            error: { message: `Limite de gardiens dans l'équipe principale atteinte (${currentGoaliesCount}/${maxGoalies})` }
          };
        }
      } else {
        console.log('Checking player position limits for main club...');
        // Pour les joueurs, récupérer la position et vérifier les limites
        const { data: playerData } = await supabase
          .from('getmyteamplayer')
          .select('positioncode')
          .eq('playerid', playerId)
          .eq('user_id', userId)
          .eq('league_id', leagueId)
          .single();

        console.log('Player position data:', playerData);

        if (!playerData) {
          console.log('ERROR: Player position not found');
          throw new Error('Player position not found');
        }

        const playerPosition = playerData.positioncode;
        console.log('Player position:', playerPosition);

        // Utiliser la vue get_number_type_player_per_team qui exclut déjà les joueurs scratch
        const { data: rosterCounts, error: rosterError } = await supabase
          .from('get_number_type_player_per_team')
          .select('number_of_defenders, number_of_fowards')
          .eq('user_id', userId)
          .eq('league_id', leagueId)
          .single();

        console.log('Current roster counts from view:', { rosterCounts, rosterError });

        if (rosterError && rosterError.code !== 'PGRST116') throw rosterError;

        const currentForwards = rosterCounts?.number_of_fowards || 0;
        const currentDefensemen = rosterCounts?.number_of_defenders || 0;

        console.log('Current position counts (excluding scratch):', { currentForwards, currentDefensemen });

        if (playerPosition === 'D') {
          const maxDefensemen = leagueSettings?.number_def || 6;
          console.log('Defenseman check:', { currentDefensemen, maxDefensemen });
          if (currentDefensemen + 1 > maxDefensemen) {
            console.log('Defenseman limit would be exceeded');
            return {
              success: false,
              error: { message: `Limite de défenseurs dans l'équipe principale atteinte (${currentDefensemen}/${maxDefensemen})` }
            };
          }
        } else if (['C', 'L', 'R'].includes(playerPosition)) {
          const maxForwards = leagueSettings?.number_foward || 12;
          console.log('Forward check:', { currentForwards, maxForwards });
          if (currentForwards + 1 > maxForwards) {
            console.log('Forward limit would be exceeded');
            return {
              success: false,
              error: { message: `Limite d'attaquants dans l'équipe principale atteinte (${currentForwards}/${maxForwards})` }
            };
          }
        }
      }

      // Vérifier si la promotion dépasserait la limite de protection par position dans le club principal
      if (leagueSettings?.protectionlist) {
        const { data: protectionEntry } = await supabase
          .from('protection_list')
          .select('playerid')
          .eq('playerid', playerId)
          .eq('user_id', userId)
          .eq('league_id', leagueId)
          .maybeSingle();

        if (protectionEntry) {
          const { data: allProtected } = await supabase
            .from('protection_list')
            .select('playerid')
            .eq('user_id', userId)
            .eq('league_id', leagueId);

          const protectedIds = (allProtected || []).map((p: any) => p.playerid);

          if (isGoalie) {
            const { data: mainProtectedGoalies } = await supabase
              .from('getmyteamgoalie')
              .select('playerid')
              .eq('user_id', userId)
              .eq('league_id', leagueId)
              .eq('isnhl', true)
              .in('playerid', protectedIds);

            const currentProtectedGoalies = (mainProtectedGoalies || []).length;
            const maxProtectedGoalies = leagueSettings?.number_goalie_protect || 1;
            if (currentProtectedGoalies >= maxProtectedGoalies) {
              return {
                success: false,
                error: { message: `Impossible de promouvoir ce gardien — il est dans votre liste de protection et la limite de gardiens protégés dans le club principal est atteinte (${currentProtectedGoalies}/${maxProtectedGoalies}). Retirez-le d'abord de la liste de protection avant d'effectuer ce mouvement.` }
              };
            }
          } else {
            const { data: mainProtectedData } = await supabase
              .from('league_players_user')
              .select('player_id')
              .eq('user_id', userId)
              .eq('league_id', leagueId)
              .eq('isnhl', true)
              .in('player_id', protectedIds);

            const mainProtectedIds = (mainProtectedData || []).map((p: any) => p.player_id);

            const { data: mainProtectedPlayers } = await supabase
              .from('getmyteamplayer')
              .select('playerid, positioncode')
              .eq('user_id', userId)
              .eq('league_id', leagueId)
              .eq('isnhl', true)
              .in('playerid', mainProtectedIds);

            const { data: posData } = await supabase
              .from('getmyteamplayer')
              .select('positioncode')
              .eq('playerid', playerId)
              .eq('user_id', userId)
              .eq('league_id', leagueId)
              .single();

            const pos = posData?.positioncode || '';

            if (pos === 'D') {
              const currentProtectedDef = (mainProtectedPlayers || []).filter((p: any) => p.positioncode === 'D').length;
              const maxProtectedDef = leagueSettings?.number_def_protect || 3;
              if (currentProtectedDef >= maxProtectedDef) {
                return {
                  success: false,
                  error: { message: `Impossible de promouvoir ce défenseur — il est dans votre liste de protection et la limite de défenseurs protégés dans le club principal est atteinte (${currentProtectedDef}/${maxProtectedDef}). Retirez-le d'abord de la liste de protection avant d'effectuer ce mouvement.` }
                };
              }
            } else if (['C', 'L', 'R'].includes(pos)) {
              const currentProtectedFwd = (mainProtectedPlayers || []).filter((p: any) => ['C', 'L', 'R'].includes(p.positioncode)).length;
              const maxProtectedFwd = leagueSettings?.number_foward_protect || 8;
              if (currentProtectedFwd >= maxProtectedFwd) {
                return {
                  success: false,
                  error: { message: `Impossible de promouvoir cet attaquant — il est dans votre liste de protection et la limite d'attaquants protégés dans le club principal est atteinte (${currentProtectedFwd}/${maxProtectedFwd}). Retirez-le d'abord de la liste de protection avant d'effectuer ce mouvement.` }
                };
              }
            }
          }
        }
      }

      console.log('Updating player to main club status...');
      // Mettre à jour pour envoyer au club principal
      const { data, error } = await supabase
        .from('league_players_user')
        .update({ 
          isnhl: true,
          is_scratch: false
        })
        .eq('player_id', playerId)
        .eq('user_id', userId)
        .eq('league_id', leagueId);

      console.log('Update result for main club:', { data, error });

      if (error) {
        console.log('ERROR updating to main club:', error);
        throw error;
      }
      
      console.log('Successfully promoted to main club');
      return { success: true, data, destination: 'main' };
    }

  } catch (error: any) {
    console.error('=== ERROR in promotePlayerFromReserves ===');
    console.error('Error details:', error);
    console.error('Error message:', error?.message);
    console.error('Error code:', error?.code);
    console.error('Error hint:', error?.hint);
    console.error('Parameters that caused error:', {
      playerId,
      userId,
      leagueId,
      destination,
      isGoalie
    });
    console.error('=== END ERROR DETAILS ===');
    return { success: false, error };
  }
}

//** Trade section end **

//** Trading Bloc section start **

// Fonction pour récupérer toutes les équipes d'une ligue
export async function getAllTeamsInLeague(leagueId: number) {
  try {
    const { data, error } = await supabase
      .from('getallteam')
      .select('*')
      .eq('league_id', leagueId);

    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('Supabase error:', error);
    return { data: null, error };
  }
}

// Fonction pour récupérer les joueurs d'une équipe avec les informations Trading Bloc
export async function getTeamPlayersWithTradingBloc(leagueId: number, userId: number) {
  try {
    // Récupérer d'abord les données de league_players_user
    const { data: leaguePlayersData, error: leaguePlayersError } = await supabase
      .from('league_players_user')
      .select(`
        id,
        player_id,
        trading_bloc,
        is_injured,
        is_scratch,
        is_intouchable,
        isnhl
      `)
      .eq('league_id', leagueId)
      .eq('user_id', userId);

    if (leaguePlayersError) throw leaguePlayersError;

    if (!leaguePlayersData || leaguePlayersData.length === 0) {
      return { data: [], error: null };
    }

    // Récupérer les IDs des joueurs (filtrer les valeurs nulles/undefined)
    const playerIds = leaguePlayersData
      .map(item => item.player_id)
      .filter(id => id != null && id !== undefined && !isNaN(id));

    console.log(`[DEBUG] Player IDs to fetch:`, playerIds);

    if (playerIds.length === 0) {
      console.log(`[DEBUG] No valid player IDs found`);
      return { data: [], error: null };
    }

    // Récupérer les détails des joueurs
    const { data: playersData, error: playersError } = await supabase
      .from('players')
      .select('playerid, skaterfullname, positioncode, teamabbrevs, points, salary')
      .in('playerid', playerIds);

    if (playersError) {
      console.warn('Erreur récupération players:', playersError);
      // Ne pas faire échouer la fonction, juste log l'erreur
    }

    // Récupérer les détails des gardiens
    const { data: goaliesData, error: goaliesError } = await supabase
      .from('goalies')
      .select('playerid, goaliefullname, teamabbrevs, points, salary')
      .in('playerid', playerIds);

    if (goaliesError) {
      console.warn('Erreur récupération goalies:', goaliesError);
      // Ne pas faire échouer la fonction, juste log l'erreur
    }

    // Joindre les données manuellement
    const allPlayers = leaguePlayersData
      .filter(leaguePlayer => leaguePlayer.player_id != null) // Filtrer les IDs invalides
      .map(leaguePlayer => {
        // Chercher dans les joueurs (convertir playerid en integer pour la comparaison)
        const playerDetails = playersData?.find(p => parseInt(p.playerid) === leaguePlayer.player_id);
        if (playerDetails) {
          return {
            id: leaguePlayer.id,
            playerid: playerDetails.playerid,
            player_id: leaguePlayer.player_id,
            name: playerDetails.skaterfullname,
            position: playerDetails.positioncode,
            team: playerDetails.teamabbrevs,
            points: playerDetails.points || 0,
            salary: playerDetails.salary || 0,
            trading_bloc: leaguePlayer.trading_bloc,
            is_injured: leaguePlayer.is_injured,
            is_scratch: leaguePlayer.is_scratch,
            is_intouchable: leaguePlayer.is_intouchable,
            isnhl: leaguePlayer.isnhl,
            isGoalie: false
          };
        }

        // Chercher dans les gardiens (convertir playerid en integer pour la comparaison)
        const goalieDetails = goaliesData?.find(g => parseInt(g.playerid) === leaguePlayer.player_id);
        if (goalieDetails) {
          return {
            id: leaguePlayer.id,
            playerid: goalieDetails.playerid,
            player_id: leaguePlayer.player_id,
            name: goalieDetails.goaliefullname,
            position: 'G',
            team: goalieDetails.teamabbrevs,
            points: goalieDetails.points || 0,
            salary: goalieDetails.salary || 0,
            trading_bloc: leaguePlayer.trading_bloc,
            is_injured: leaguePlayer.is_injured,
            is_scratch: leaguePlayer.is_scratch,
            is_intouchable: leaguePlayer.is_intouchable,
            isnhl: leaguePlayer.isnhl,
            isGoalie: true
          };
        }

        // Si aucun détail trouvé, retourner null pour filtrer plus tard
        console.warn(`[DEBUG] No details found for player_id: ${leaguePlayer.player_id}`);
        return null;
      })
      .filter(player => player !== null); // Filtrer les joueurs non trouvés

    // DEBUG: Log des données récupérées
    console.log(`[DEBUG] getTeamPlayersWithTradingBloc - Données récupérées:`, {
      leagueId,
      userId,
      totalPlayers: allPlayers.length,
      playersWithTradingBlocTrue: allPlayers.filter(p => p.trading_bloc === true).length,
      sampleData: allPlayers.slice(0, 3).map(p => ({
        name: p.name,
        trading_bloc: p.trading_bloc,
        is_intouchable: p.is_intouchable,
        position: p.position
      }))
    });

    return { data: allPlayers, error: null };
  } catch (error: any) {
    console.error('Error in getTeamPlayersWithTradingBloc:', error);
    return { data: null, error };
  }
}

// Fonction pour récupérer tous les joueurs d'une équipe spécifique
export async function getTeamPlayers(leagueId: number, userId: number) {
  try {
    const [playersRes, goaliesRes] = await Promise.all([
      supabase
        .from('getmyteamplayer')
        .select('*')
        .eq('league_id', leagueId)
        .eq('user_id', userId),
      supabase
        .from('getmyteamgoalie')
        .select('*')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
    ]);

    if (playersRes.error) throw playersRes.error;
    if (goaliesRes.error) throw goaliesRes.error;

    const allPlayers = [
      ...(playersRes.data || []).map(player => ({
        ...player,
        isGoalie: false,
        name: player.skaterfullname,
        position: player.positioncode,
        team: player.teamabbrevs,
        points: player.points || 0
      })),
      ...(goaliesRes.data || []).map(goalie => ({
        ...goalie,
        isGoalie: true,
        name: goalie.goaliefullname,
        position: 'G',
        team: goalie.teamabbrevs,
        points: goalie.points || 0
      }))
    ];

    return { data: allPlayers, error: null };
  } catch (error: any) {
    console.error('Supabase error:', error);
    return { data: null, error };
  }
}

// Fonction pour mettre à jour le statut trading bloc d'un joueur
export async function updatePlayerTradingStatus(
  leagueId: number,
  playerId: number,
  userId: number,
  tradingBloc: boolean
) {
  try {
    const { data, error } = await supabase
      .from('league_players_user')
      .update({ trading_bloc: tradingBloc })
      .eq('league_id', leagueId)
      .eq('player_id', playerId)
      .eq('user_id', userId);

    if (error) throw error;

    return { success: true, data, error: null };
  } catch (error: any) {
    console.error('Error updating trading status:', error);
    return { success: false, data: null, error };
  }
}

// Fonction pour mettre à jour le statut intouchable d'un joueur
export async function updatePlayerIntouchableStatus(
  leagueId: number,
  playerId: number,
  userId: number,
  isIntouchable: boolean
) {
  try {
    const { data, error } = await supabase
      .from('league_players_user')
      .update({ is_intouchable: isIntouchable })
      .eq('league_id', leagueId)
      .eq('player_id', playerId)
      .eq('user_id', userId);

    if (error) throw error;

    return { success: true, data, error: null };
  } catch (error: any) {
    console.error('Error updating intouchable status:', error);
    return { success: false, data: null, error };
  }
}

// Fonction pour récupérer les données complètes du Trading Bloc
export async function getTradingBlocData(leagueId: number) {
  try {
    // 1. Récupérer toutes les équipes de la ligue
    const teamsResult = await getAllTeamsInLeague(leagueId);
    if (teamsResult.error || !teamsResult.data) {
      throw teamsResult.error || new Error('No teams found');
    }

    // 2. Pour chaque équipe, récupérer ses joueurs
    const tradingData = await Promise.all(
      teamsResult.data.map(async (team) => {
        const playersResult = await getTeamPlayersWithTradingBloc(leagueId, team.users_user_id);
        const players = playersResult.data || [];

        // DEBUG: Ajouter des logs pour diagnostiquer
        console.log(`[DEBUG] Équipe ${team.username}:`, {
          totalPlayers: players.length,
          playersWithTradingBloc: players.filter(p => p.trading_bloc === true).length,
          playersData: players.map(p => ({
            name: p.name,
            trading_bloc: p.trading_bloc,
            position: p.position
          }))
        });

        // Séparer les joueurs disponibles et intouchables
        // Logique simplifiée : 
        // 1. Si is_intouchable = true, alors INTOUCHABLE (priorité absolue)
        // 2. SEULEMENT les joueurs avec trading_bloc = true sont DISPONIBLES
        const availablePlayers = players.filter(player => {
          // Si explicitement marqué comme intouchable, ne pas inclure
          if (player.is_intouchable === true) return false;
          
          // Afficher SEULEMENT les joueurs explicitement marqués dans le trading bloc
          return player.trading_bloc === true;
        });

        // DEBUG: Log des joueurs disponibles
        console.log(`[DEBUG] Joueurs disponibles pour ${team.username}:`, availablePlayers.map(p => ({
          name: p.name,
          trading_bloc: p.trading_bloc,
          position: p.position
        })));

        const untouchablePlayers = players.filter(player => {
          // Seuls les joueurs explicitement marqués comme intouchables
          return player.is_intouchable === true;
        });

        return {
          id: team.users_user_id,
          teamName: `Équipe ${team.username}`, // Préfixer pour clarifier
          managerName: team.username,
          availablePlayers: availablePlayers.map(player => ({
            id: player.playerid || player.player_id,
            name: player.name,
            position: player.position,
            team: player.team,
            points: player.points,
            salary: player.salary || 0,
            status: 'available' as const,
            isInjured: player.is_injured || false,
            isScratch: player.is_scratch || false,
            tradingBloc: player.trading_bloc,
            isIntouchable: player.is_intouchable || false,
            leaguePlayerId: player.id // ID de la table league_players_user pour les mises à jour
          })),
          untouchablePlayers: untouchablePlayers.map(player => ({
            id: player.playerid || player.player_id,
            name: player.name,
            position: player.position,
            team: player.team,
            points: player.points,
            salary: player.salary || 0,
            status: 'untouchable' as const,
            isInjured: player.is_injured || false,
            isScratch: player.is_scratch || false,
            tradingBloc: player.trading_bloc,
            isIntouchable: player.is_intouchable || false,
            leaguePlayerId: player.id // ID de la table league_players_user pour les mises à jour
          }))
        };
      })
    );

    return { data: tradingData, error: null };
  } catch (error: any) {
    console.error('Supabase error:', error);
    return { data: null, error };
  }
}

// Fonction pour récupérer les informations de masse salariale d'une équipe
export async function getTeamSalaryCapInfo(leagueId: number, userId: number) {
  try {
    // Récupérer les paramètres de la ligue
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('salary_cap, max_salary_cap, salary_cap_reserve, max_salary_cap_reserve')
      .eq('league_id', leagueId)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

    // Si les plafonds salariaux ne sont pas activés
    if (!leagueSettings?.salary_cap || !leagueSettings?.max_salary_cap) {
      return {
        data: {
          isSalaryCapEnabled: false,
          mainClub: {
            current: 0,
            max: 0,
            available: 0,
            percentage: 0
          },
          reserves: {
            current: 0,
            max: 0,
            available: 0,
            percentage: 0
          }
        },
        error: null
      };
    }

    // Récupérer la masse salariale actuelle du club principal
    const { data: mainCapData, error: mainCapError } = await supabase
      .from('get_salary_cap_per_team')
      .select('salary_cap')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('isnhl', true)
      .single();

    if (mainCapError && mainCapError.code !== 'PGRST116') throw mainCapError;

    const mainClubSalary = mainCapData?.salary_cap || 0;
    const maxMainSalary = leagueSettings.max_salary_cap;
    const availableMainSalary = maxMainSalary - mainClubSalary;
    const mainPercentage = maxMainSalary > 0 ? (mainClubSalary / maxMainSalary) * 100 : 0;

    // Récupérer la masse salariale actuelle des réserves (si activée)
    let reservesInfo = {
      current: 0,
      max: 0,
      available: 0,
      percentage: 0
    };

    if (leagueSettings?.salary_cap_reserve && leagueSettings?.max_salary_cap_reserve) {
      const { data: reserveCapData, error: reserveCapError } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('isnhl', false)
        .single();

      if (reserveCapError && reserveCapError.code !== 'PGRST116') throw reserveCapError;

      const reservesSalary = reserveCapData?.salary_cap || 0;
      const maxReservesSalary = leagueSettings.max_salary_cap_reserve;
      const availableReservesSalary = maxReservesSalary - reservesSalary;
      const reservesPercentage = maxReservesSalary > 0 ? (reservesSalary / maxReservesSalary) * 100 : 0;

      reservesInfo = {
        current: reservesSalary,
        max: maxReservesSalary,
        available: availableReservesSalary,
        percentage: reservesPercentage
      };
    }

    return {
      data: {
        isSalaryCapEnabled: true,
        mainClub: {
          current: mainClubSalary,
          max: maxMainSalary,
          available: availableMainSalary,
          percentage: mainPercentage
        },
        reserves: reservesInfo
      },
      error: null
    };
  } catch (error: any) {
    console.error('Error getting team salary cap info:', error);
    return { data: null, error };
  }
}

//** Trading Bloc section end **

//** Draft Picks Generation section start **

// Fonction pour générer les choix de repêchage pour une ligue
export async function generateDraftPicksForLeague(leagueId: number, numberOfRounds: number) {
  try {
    const { data, error } = await supabase
      .rpc('generate_draft_picks_for_league', { 
        p_league_id: leagueId,
        p_number_of_rounds: numberOfRounds
      });
    
    if (error) throw error;
    
    if (data && data.success) {
      return { success: true, data: data.data };
    } else {
      return { success: false, error: data?.error || { message: 'Erreur inconnue' } };
    }
  } catch (error: any) {
    console.error('Supabase error:', error);
    return { success: false, error: { message: error.message || 'Erreur lors de la génération des choix' } };
  }
}

//** Draft Picks Generation section end **

//** Draft Picks Trading section start **

// Fonction pour récupérer les choix de repêchage d'un utilisateur
export async function getUserDraftPicks(leagueId: number, userId: number) {
  try {
    // D'abord récupérer les picks de l'utilisateur avec les informations de base
    const { data: userPicks, error: userPicksError } = await supabase
      .from('draftpicks_user_league')
      .select('id, draftpicks_id, original_user_id')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .order('draftpicks_id');
    
    if (userPicksError && userPicksError.code !== 'PGRST116') throw userPicksError;
    
    if (!userPicks || userPicks.length === 0) {
      return { data: [], error: null };
    }
    
    // Récupérer les descriptions des picks
    const pickIds = userPicks.map(pick => pick.draftpicks_id);
    const { data: pickDescriptions, error: descError } = await supabase
      .from('draftpickstext')
      .select('drafrpick_id, description_fr')
      .in('drafrpick_id', pickIds);
    
    if (descError && descError.code !== 'PGRST116') throw descError;

    // Récupérer les noms d'utilisateurs originaux (uniquement pour ceux qui ont un original_user_id)
    const originalUserIds = [...new Set(userPicks
      .map(pick => pick.original_user_id)
      .filter(id => id !== null && id !== undefined))];
    
    let originalUsers: any[] = [];
    if (originalUserIds.length > 0) {
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('user_id, username')
        .in('user_id', originalUserIds);
      
      if (usersError && usersError.code !== 'PGRST116') {
        console.warn('Error fetching original users:', usersError);
      } else {
        originalUsers = usersData || [];
      }
    }
    
    // Combiner les données
    const transformedData = userPicks.map((pick: any) => {
      const description = pickDescriptions?.find(desc => desc.drafrpick_id === pick.draftpicks_id);
      const originalUser = originalUsers.find(user => user.user_id === pick.original_user_id);
      
      return {
        id: pick.id,
        draftPickId: pick.draftpicks_id,
        description: description?.description_fr || `${pick.draftpicks_id}e ronde`,
        roundNumber: pick.draftpicks_id || 0,
        originalUserId: pick.original_user_id,
        originalUserName: originalUser?.username || null
      };
    });

    return { data: transformedData, error: null };
  } catch (error: any) {
    console.error('Supabase error getting draft picks:', error);
    return { data: null, error };
  }
}

//** Draft Picks Trading section end **

// ── Trade Placement Preferences ─────────────────────────────────────────────
// Saves which roster section (main or reserve) a user wants for each player
// they will receive in a pending trade.  Called right after trade proposal.
// The acceptant's side calls this too after the placement popup on acceptance.
export async function saveTradePreferences(
  tradesId: number,
  leagueId: number,
  forUserId: number,
  placements: Record<string, 'main' | 'reserve'>
): Promise<{ success: boolean; error?: any }> {
  try {
    const rows = Object.entries(placements)
      .filter(([, placement]) => !!placement)
      .map(([playerId, placement]) => ({
        trades_id: tradesId,
        league_id: leagueId,
        for_user_id: forUserId,
        player_id: String(playerId),
        placement,
      }));

    if (rows.length === 0) return { success: true };

    const { error } = await supabase
      .from('trade_placement_preferences')
      .insert(rows);

    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('Error saving trade placement preferences:', error);
    return { success: false, error };
  }
}

// Fetches stored placement preferences for a trade group and applies them by
// updating league_players_user.isnhl, then deletes the rows from the table.
// Called from GroupedTradeCard after processCompleteTrade succeeds.
export async function applyAndClearTradePlacements(
  tradesId: number,
  leagueId: number
): Promise<{ success: boolean; error?: any }> {
  try {
    const { data: prefs, error: fetchError } = await supabase
      .from('trade_placement_preferences')
      .select('player_id, placement, for_user_id')
      .eq('trades_id', tradesId)
      .eq('league_id', leagueId);

    if (fetchError) throw fetchError;

    if (prefs && prefs.length > 0) {
      for (const pref of prefs) {
        await supabase
          .from('league_players_user')
          .update({ isnhl: pref.placement === 'main', is_scratch: false })
          .eq('player_id', parseInt(pref.player_id))
          .eq('league_id', leagueId)
          .eq('user_id', pref.for_user_id);
      }

      // Clean up after applying
      await supabase
        .from('trade_placement_preferences')
        .delete()
        .eq('trades_id', tradesId)
        .eq('league_id', leagueId);
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error applying trade placement preferences:', error);
    return { success: false, error };
  }
}
// ── End Trade Placement Preferences ─────────────────────────────────────────

//** FAQ Section Start **

// Fonction pour récupérer les questions FAQ d'une ligue
export async function getFAQQuestions(leagueId: number) {
  try {
    const { data, error } = await supabase
      .rpc('get_faq_questions_by_league', { p_league_id: leagueId });
    
    if (error && error.code !== 'PGRST116') throw error;
    return { data: data || [], error: null };
  } catch (error: any) {
    console.error('Supabase error getting FAQ questions:', error);
    return { data: [], error };
  }
}

// Fonction pour récupérer les réponses d'une question FAQ
export async function getFAQAnswers(questionId: number) {
  try {
    const { data, error } = await supabase
      .rpc('get_faq_answers_by_question', { p_question_id: questionId });
    
    if (error && error.code !== 'PGRST116') throw error;
    return { data: data || [], error: null };
  } catch (error: any) {
    console.error('Supabase error getting FAQ answers:', error);
    return { data: [], error };
  }
}

// Fonction pour créer une nouvelle question FAQ
export async function createFAQQuestion(
  leagueId: number,
  question: string,
  category: string = 'general',
  priority: string = 'normal'
) {
  try {
    const { data, error } = await supabase
      .rpc('create_faq_question', {
        p_league_id: leagueId,
        p_question: question,
        p_category: category,
        p_priority: priority
      });

    if (error) throw error;

    const result = typeof data === 'string' ? JSON.parse(data) : data;
    
    if (!result.success) {
      return { 
        success: false, 
        error: { message: result.error?.message || 'Erreur lors de la création de la question' }
      };
    }

    return { success: true, data: result.data };
  } catch (error: any) {
    console.error('Supabase error creating FAQ question:', error);
    return { 
      success: false, 
      error: { message: error.message || 'Erreur lors de la création de la question' }
    };
  }
}

// Fonction pour rechercher des joueurs avec des critères avancés
export async function searchPlayersAdvanced(params: {
  leagueId: number;
  term?: string;
  minSalary?: number;
  maxSalary?: number;
  minGoals?: number;
  maxGoals?: number;
  minAssists?: number;
  maxAssists?: number;
  minPoints?: number;
  maxPoints?: number;
  positions?: string[];
  statuses?: string[];
  teamAbbrevs?: string[];
  minAge?: number;
  maxAge?: number;
  minYearsLeft?: number;
  maxYearsLeft?: number;
  orderBy?: string;
  limit?: number;
  offset?: number;
}) {
  try {
    const { data, error } = await supabase.rpc('search_players_advanced', {
      p_league_id: params.leagueId,
      p_term: params.term || null,
      p_min_salary: params.minSalary || null,
      p_max_salary: params.maxSalary || null,
      p_min_goals: params.minGoals || null,
      p_max_goals: params.maxGoals || null,
      p_min_assists: params.minAssists || null,
      p_max_assists: params.maxAssists || null,
      p_min_points: params.minPoints || null,
      p_max_points: params.maxPoints || null,
      p_positions: params.positions || null,
      p_statuses: params.statuses || null,
      p_team_abbrevs: params.teamAbbrevs || null,
      p_min_age: params.minAge || null,
      p_max_age: params.maxAge || null,
      p_min_years_left: params.minYearsLeft || null,
      p_max_years_left: params.maxYearsLeft || null,
      p_order_by: params.orderBy || 'points_desc',
      p_limit: params.limit || 50,
      p_offset: params.offset || 0
    });

    if (error && error.code !== 'PGRST116') throw error;
    return { data: data || [], error: null };
  } catch (error: any) {
    console.error('Error in searchPlayersAdvanced:', error);
    return { data: [], error };
  }
}

// Fonction pour créer une nouvelle réponse FAQ
export async function createFAQAnswer(
  questionId: number,
  answer: string,
  isOfficial: boolean = true
) {
  try {
    const { data, error } = await supabase
      .rpc('create_faq_answer', {
        p_question_id: questionId,
        p_answer: answer,
        p_is_official: isOfficial
      });

    if (error) throw error;

    const result = typeof data === 'string' ? JSON.parse(data) : data;
    
    if (!result.success) {
      return { 
        success: false, 
        error: { message: result.error?.message || 'Erreur lors de la création de la réponse' }
      };
    }

    return { success: true, data: result.data };
  } catch (error: any) {
    console.error('Supabase error creating FAQ answer:', error);
    return { 
      success: false, 
      error: { message: error.message || 'Erreur lors de la création de la réponse' }
    };
  }
}

// Fonction pour mettre à jour le statut d'une question FAQ
export async function updateFAQQuestionStatus(
  questionId: number,
  status: string,
  isPinned?: boolean
) {
  try {
    const { data, error } = await supabase
      .rpc('update_faq_question_status', {
        p_question_id: questionId,
        p_status: status,
        p_is_pinned: isPinned
      });

    if (error) throw error;

    const result = typeof data === 'string' ? JSON.parse(data) : data;
    
    if (!result.success) {
      return { 
        success: false, 
        error: { message: result.error?.message || 'Erreur lors de la mise à jour' }
      };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Supabase error updating FAQ question:', error);
    return { 
      success: false, 
      error: { message: error.message || 'Erreur lors de la mise à jour' }
    };
  }
}

// Fonction pour incrémenter le nombre de vues d'une question
export async function incrementFAQQuestionViews(questionId: number) {
  try {
    await supabase.rpc('increment_faq_question_views', { 
      p_question_id: questionId 
    });
    return { success: true };
  } catch (error: any) {
    console.error('Supabase error incrementing views:', error);
    return { success: false };
  }
}

//** FAQ Section End **

//** Payment System Section Start **

// Vérifier le statut de paiement d'un utilisateur
export async function checkUserPaymentStatus(leagueId: number, userId: number) {
  try {
    console.log('Checking payment status for:', { leagueId, userId });
    
    // Utiliser select() sans .single() pour éviter l'erreur si aucun résultat
    const { data, error } = await supabase
      .from('league_payments')
      .select('status, amount, payment_type, subscription_active, next_payment_date, created_at, updated_at')
      .eq('league_id', leagueId)
      .eq('user_id', userId);

    console.log('Payment query result:', { data, error });

    if (error) {
      console.error('Payment status error:', error);
      throw error;
    }
    
    // Si aucun paiement trouvé, l'utilisateur n'a pas payé
    const paymentRecord = data && data.length > 0 ? data[0] : null;
    
    // Pour les paiements récurrents, vérifier si l'abonnement est actif
    let hasPaid = false;
    if (paymentRecord?.status === 'completed') {
      if (paymentRecord.payment_type === 'recurring') {
        // Autoriser si abonnement actif OU si la date de prochain paiement n'est pas dépassée
        const today = new Date();
        const nextPaymentDate = paymentRecord.next_payment_date ? new Date(paymentRecord.next_payment_date) : null;
  hasPaid = paymentRecord.subscription_active === true || (!!nextPaymentDate && nextPaymentDate >= today);
      } else {
        // Pour les paiements uniques, juste vérifier le statut
        hasPaid = true;
      }
    }
    
    const result = { 
      success: true,
      hasPaid,
      paymentData: paymentRecord,
      isRecurring: paymentRecord?.payment_type === 'recurring',
      subscriptionActive: paymentRecord?.subscription_active || false,
      nextPaymentDate: paymentRecord?.next_payment_date
    };
    
    console.log('Final payment result:', result);
    return result;
  } catch (error: any) {
    console.error('Error checking payment status:', error);
    return { success: false, hasPaid: false, paymentData: null, isRecurring: false, subscriptionActive: false };
  }
}

// Initier un paiement de ligue (unique ou récurrent)
export async function initiateLeaguePayment(leagueId: number, userId: number, recurring: boolean = true) {
  try {
    // Récupérer les paramètres de la ligue
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('league_fee, require_payment')
      .eq('league_id', leagueId)
      .single();

    if (settingsError) throw settingsError;

    if (!leagueSettings.require_payment) {
      return { success: true, paymentRequired: false };
    }

    // Récupérer les infos de la ligue
    const { data: league, error: leagueError } = await supabase
      .from('leagues')
      .select('name')
      .eq('league_id', leagueId)
      .single();

    if (leagueError) throw leagueError;

    // Importer le service Stripe de façon dynamique
    const { createPaymentSession } = await import('./services/stripeService');

    // Créer la session de paiement avec le paramètre récurrent
    await createPaymentSession({
      leagueId,
      userId,
      amount: leagueSettings.league_fee,
      leagueName: league.name,
      successUrl: `${window.location.origin}/league-payment-success?league=${leagueId}`,
      cancelUrl: `${window.location.origin}/select-league`,
      recurring // Nouveau paramètre pour activer les paiements récurrents
    });

    return { success: true, paymentRequired: true, recurring };
  } catch (error: any) {
    console.error('Error initiating payment:', error);
    return { success: false, error: { message: 'Erreur lors de l\'initialisation du paiement' } };
  }
}

// Fonction pour sélectionner une ligue avec vérification de paiement
export async function selectLeagueWithPaymentCheck(leagueId: number, userId: number) {
  try {
    // Vérifier que l'utilisateur est bien membre de la ligue via leagues_users
    const { data: membership, error: membershipError } = await supabase
      .from('leagues_users')
      .select('user_id')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .single();

    if (membershipError && membershipError.code !== 'PGRST116') {
      throw membershipError;
    }

    if (!membership) {
      return {
        success: false,
        error: { message: 'Vous n\'êtes pas membre de cette ligue' }
      };
    }

    // Récupérer les paramètres de paiement de la ligue séparément
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('require_payment, league_fee')
      .eq('league_id', leagueId)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') {
      console.warn('Erreur lors de la récupération des paramètres:', settingsError);
    }

    // Si paiement requis, vérifier le statut de paiement
    if (leagueSettings?.require_payment) {
      // user_id 3 n'a pas besoin de payer
      if (userId !== 3) {
        const { hasPaid } = await checkUserPaymentStatus(leagueId, userId);
        if (!hasPaid) {
          return {
            success: false,
            requiresPayment: true,
            leagueId: leagueId,
            amount: leagueSettings.league_fee,
            error: { message: 'Paiement requis pour accéder à cette ligue' }
          };
        }
      }
    }

    return {
      success: true,
      message: 'Ligue sélectionnée avec succès'
    };

  } catch (error: any) {
    console.error('Error selecting league:', error);
    return {
      success: false,
      error: { message: 'Erreur lors de la sélection de la ligue' }
    };
  }
}

// Annuler un abonnement récurrent
export async function cancelSubscription(leagueId: number, userId: number) {
  try {
    console.log('Debug - Searching for subscription:', { leagueId, userId });

    const paymentQuery = `SELECT stripe_subscription_id, payment_type, subscription_active, status
FROM league_payments
WHERE league_id = ${leagueId}
  AND user_id = ${userId}
  AND payment_type = 'recurring'`;
    console.log('Debug - SQL paymentData:', paymentQuery);
    
    // Récupérer l'abonnement de l'utilisateur
    const { data: paymentData, error: paymentError } = await supabase
      .from('league_payments')
      .select('stripe_subscription_id, payment_type, subscription_active, status, created_at, updated_at')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('payment_type', 'recurring')
      .order('created_at', { ascending: false });

    console.log('Debug - Payment query result:', { paymentData, paymentError });

    if (paymentError && paymentError.code !== 'PGRST116') {
      throw paymentError;
    }

    // Chercher un abonnement actif ou récemment créé
    const activeSubscription = paymentData?.find(p => 
      p.subscription_active === true && p.stripe_subscription_id
    );

    const fallbackByStatus = paymentData?.find(p => 
      p.status === 'completed' && p.stripe_subscription_id
    );

    const fallbackAnyStripeId = paymentData?.find(p => Boolean(p.stripe_subscription_id));

    const subscriptionToCancel = activeSubscription || fallbackByStatus || fallbackAnyStripeId;

    console.log('Debug - Active subscription found:', activeSubscription);
    console.log('Debug - Fallback subscription (status):', fallbackByStatus);
    console.log('Debug - Fallback subscription (any):', fallbackAnyStripeId);

    if (!subscriptionToCancel || !subscriptionToCancel.stripe_subscription_id) {
      return {
        success: false,
        error: { message: 'Aucun abonnement actif trouvé pour cette ligue' }
      };
    }

    // Importer le service Stripe
    const { cancelSubscription: stripeCancelSubscription } = await import('./services/stripeService');
    
    // Annuler l'abonnement sur Stripe — la Edge Function détermine le subscriptionId depuis la DB
    await stripeCancelSubscription(leagueId, userId);

    // Mettre à jour la base de données pour marquer l'abonnement comme annulé
    const { error: updateError } = await supabase
      .from('league_payments')
      .update({ 
        subscription_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('league_id', leagueId)
      .eq('user_id', userId)
  .eq('stripe_subscription_id', subscriptionToCancel.stripe_subscription_id);

    if (updateError) {
      console.error('Error updating payment record after cancellation:', updateError);
      // L'annulation Stripe a réussi, donc on continue même si la mise à jour DB échoue
    }

    return {
      success: true,
      message: 'Abonnement annulé avec succès. Vous conserverez l\'accès jusqu\'à la fin de la période payée.'
    };

  } catch (error: any) {
    console.error('Error cancelling subscription:', error);
    return {
      success: false,
      error: { message: 'Erreur lors de l\'annulation de l\'abonnement' }
    };
  }
}

// Obtenir les informations d'abonnement d'un utilisateur
export async function getSubscriptionInfo(leagueId: number, userId: number) {
  try {
    const { data: paymentData, error } = await supabase
      .from('league_payments')
      .select('*')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('payment_type', 'recurring')
      .single();

    if (error && error.code !== 'PGRST116') {
      // Detect specific case where PostgREST can't find function signature
      if (error.code === 'PGRST202') {
        console.error('RPC signature mismatch calling place_trade_bid:', error);
        // Provide a clearer client-side error message
        throw new Error('RPC signature mismatch: vérifiez les paramètres envoyés à place_trade_bid');
      }
      throw error;
    }

    return {
      success: true,
      subscription: paymentData || null,
      hasActiveSubscription: paymentData?.subscription_active || false
    };

  } catch (error: any) {
    console.error('Error getting subscription info:', error);
    return {
      success: false,
      subscription: null,
      hasActiveSubscription: false
    };
  }
}

// Accepter une demande d'adhésion avec vérification de paiement
export async function acceptJoinRequest(requestId: number, userId: number) {
  try {
    // Récupérer la demande
    const { data: joinRequest, error: requestError } = await supabase
      .from('league_invitation')
      .select('*')
      .eq('id', requestId)
      .eq('user_id', userId)
      .single();

    if (requestError) throw requestError;

    // Récupérer les paramètres de la ligue séparément
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('require_payment, league_fee')
      .eq('league_id', joinRequest.league_id)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') {
      console.warn('Erreur lors de la récupération des paramètres:', settingsError);
    }

    // Accepter la demande en ajoutant l'utilisateur à leagues_users
    const { error: acceptError } = await supabase
      .from('leagues_users')
      .insert({
        league_id: joinRequest.league_id,
        user_id: userId
      });

    if (acceptError) {
      // Si l'utilisateur existe déjà, c'est OK
      if (!acceptError.message?.includes('duplicate key')) {
        throw acceptError;
      }
    }

    // Supprimer la demande d'invitation après acceptation
    await supabase
      .from('league_invitation')
      .delete()
      .eq('id', requestId);

    // Vérifier si un paiement est requis
    if (leagueSettings?.require_payment) {
      const { hasPaid } = await checkUserPaymentStatus(joinRequest.league_id, userId);
      
      if (!hasPaid) {
        return { 
          success: true, 
          requiresPayment: true,
          leagueId: joinRequest.league_id,
          message: 'Demande acceptée. Paiement requis pour participer pleinement.'
        };
      }
    }

    return { success: true, message: 'Demande d\'adhésion acceptée avec succès!' };
  } catch (error: any) {
    console.error('Error accepting join request:', error);
    return { success: false, error: { message: 'Erreur lors de l\'acceptation de la demande' } };
  }
}

// Quitter une ligue en transférant l'équipe au DG tutelle
export async function leaveLeagueAssignGuardian(leagueId: number, userId: number) {
  try {
    const { data, error } = await supabase.rpc('leave_league_assign_guardian', {
      p_league_id: leagueId,
      p_user_id: userId,
    });

    if (error) {
      throw error;
    }

    if (!data?.success) {
      const message = data?.error?.message || 'Impossible de quitter la ligue.';
      return { success: false, error: { message } };
    }

    let subscriptionStatus: 'success' | 'not-found' | 'error' = 'not-found';
    let subscriptionMessage: string | null = null;

    const cancelResult = await cancelSubscription(leagueId, userId);

    if (cancelResult.success) {
      subscriptionStatus = 'success';
      subscriptionMessage =
        cancelResult.message || 'Votre abonnement récurrent a été annulé automatiquement.';
    } else {
      const cancelErrorMessage = cancelResult.error?.message;

      if (cancelErrorMessage === 'Aucun abonnement actif trouvé pour cette ligue') {
        subscriptionStatus = 'not-found';
        subscriptionMessage =
          "Aucun abonnement récurrent actif n'a été détecté pour cette ligue.";
      } else {
        subscriptionStatus = 'error';
        subscriptionMessage = `L'annulation de l'abonnement a échoué : ${
          cancelErrorMessage || 'Une erreur inattendue est survenue.'
        }`;
        console.warn('Annulation abonnement lors de la sortie de ligue:', cancelResult.error);
      }
    }

    const baseMessage = data.message || 'Votre équipe a été transférée au DG tutelle.';
    const combinedMessage = subscriptionMessage
      ? `${baseMessage} ${subscriptionMessage}`.trim()
      : baseMessage;

    return {
      success: true,
      guardianUserId: data.guardian_user_id,
      transferredPlayers: data.transferred_players ?? 0,
      transferredPicks: data.transferred_picks ?? 0,
      subscriptionStatus,
      subscriptionMessage,
      message: combinedMessage,
    };
  } catch (error: any) {
    console.error('Error leaving league:', error);
    return {
      success: false,
      error: { message: error.message || 'Erreur lors de la sortie de la ligue.' },
    };
  }
}

export async function assignGuardianTeamToUser(leagueId: number, targetUserId: number, sourceGuardianUserId?: number) {
  try {
    const { data, error } = await supabase.rpc('assign_guardian_team_to_user', {
      p_league_id: leagueId,
      p_target_user_id: targetUserId,
      p_source_guardian_user_id: sourceGuardianUserId ?? null,
    });

    if (error) {
      throw error;
    }

    if (!data?.success) {
      const message = data?.error?.message || 'Impossible d\'assigner l\'équipe tutelle.';
      return { success: false, error: { message } };
    }

    return {
      success: true,
      transferredPlayers: data.transferred_players ?? 0,
      transferredPicks: data.transferred_picks ?? 0,
      message: data.message || 'L\'équipe tutelle a été assignée avec succès.',
    };
  } catch (error: any) {
    console.error('Error assigning guardian team:', error);
    return {
      success: false,
      error: { message: error.message || 'Erreur lors de l\'assignation de l\'équipe tutelle.' },
    };
  }
}

//** Payment System Section End **

//** Trade Auctions Section Start **

// Créer une enchère pour un échange
export async function createTradeAuction(
  leagueId: number,
  originalTradeId: number,
  initiatorUserId: number,
  targetUserId: number,
  senderAssets: any,
  receiverAssets: any
) {
  try {
    const { data, error } = await supabase.rpc('create_trade_auction', {
      p_league_id: leagueId,
      p_original_trade_id: originalTradeId,
      p_initiator_user_id: initiatorUserId,
      p_target_user_id: targetUserId,
      p_sender_assets: senderAssets,
      p_receiver_assets: receiverAssets
    });

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Erreur création enchère:', error);
    return { data: null, error };
  }
}

// Récupérer les enchères actives
export async function getActiveAuctions() {
  try {
    const { data, error } = await supabase.rpc('get_active_auctions');

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return { data: data || [], error: null };
  } catch (error: any) {
    console.error('Erreur récupération enchères actives:', error);
    return { data: [], error };
  }
}

// Récupérer les détails d'une enchère
export async function getAuctionDetails(auctionId: number) {
  try {
    const { data, error } = await supabase.rpc('get_auction_details', {
      p_auction_id: auctionId
    });

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Erreur récupération détails enchère:', error);
    return { data: null, error };
  }
}

// Placer une offre concurrente
export async function placeTradeBid(
  auctionId: number,
  bidderUserId: number,
  bidSenderAssets: any,
  bidReceiverAssets: any,
  targetSide?: 'initiator' | 'target'
) {
  try {
    // Validate auctionId early to avoid calling RPC with shifted params
    if (auctionId == null || auctionId === undefined || isNaN(Number(auctionId))) {
      const err = new Error('Missing or invalid auctionId when placing trade bid');
      console.error(err);
      return { data: null, error: err };
    }

    const payload = {
      p_auction_id: Number(auctionId),
      p_bidder_user_id: bidderUserId,
      p_bid_sender_assets: bidSenderAssets,
      p_bid_receiver_assets: bidReceiverAssets,
      p_target_side: targetSide ?? null
    } as any;
    // Debug log minimal payload shape (no secrets)
    console.debug('Calling RPC place_trade_bid with payload keys:', Object.keys(payload));

    const { data, error } = await supabase.rpc('place_trade_bid', payload);

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Erreur placement offre:', error);
    return { data: null, error };
  }
}

// Accepter une offre concurrente
export async function acceptTradeBid(bidId: number, acceptingUserId: number) {
  try {
    const { data, error } = await supabase.rpc('accept_trade_bid', {
      p_bid_id: bidId,
      p_accepting_user_id: acceptingUserId
    });

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Erreur acceptation offre:', error);
    return { data: null, error };
  }
}

// Rejeter/annuler une acceptation d'offre
export async function rejectTradeBid(bidId: number, rejectingUserId: number) {
  try {
    const { data, error } = await supabase.rpc('reject_trade_bid', {
      p_bid_id: bidId,
      p_rejecting_user_id: rejectingUserId
    });

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Erreur rejet offre:', error);
    return { data: null, error };
  }
}

// Exécuter les enchères expirées (pour les scripts automatiques)
export async function executeExpiredAuctions() {
  try {
    const { data, error } = await supabase.rpc('execute_expired_auctions');

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Erreur exécution enchères expirées:', error);
    return { data: null, error };
  }
}

//** Trade Auctions Section End **

// Récupérer les informations contractuelles d'un joueur (salaire et années restantes)
export async function getPlayerContractInfo(playerId: number) {
  try {
    // Essayer d'abord dans la vue des joueurs (données du jour actuel)
    const { data: playerData } = await supabase
      .from('show_player_onlycurrentday')
      .select('salary, yearleft')
      .eq('playerid', playerId)
      .single();

    if (playerData) {
      return { 
        data: {
          salary: playerData.salary,
          yearleft: playerData.yearleft
        }, 
        error: null 
      };
    }

    // Si pas trouvé dans les joueurs, chercher dans les gardiens
    const { data: goalieData } = await supabase
      .from('show_goalie_onlycurrentday')
      .select('salary, yearleft')
      .eq('playerid', playerId)
      .single();

    if (goalieData) {
      return { 
        data: {
          salary: goalieData.salary,
          yearleft: goalieData.yearleft
        }, 
        error: null 
      };
    }

    // Joueur non trouvé
    return { data: null, error: null };
  } catch (error: any) {
    console.error('Erreur récupération infos contrat:', error);
    return { data: null, error };
  }
}

// Fonction pour réactiver un joueur blessé sans permutation
export async function reactivateInjuredPlayerDirect(
  leagueId: number,
  userId: number,
  injuredPlayerId: number,
  isGoalie: boolean,
  destination: 'MAIN' | 'RESERVES' = 'MAIN',
  options?: { enforceSalaryCap?: boolean }
) {
  try {
    const enforceSalaryCap = options?.enforceSalaryCap === true;

    const { data: injuredPlayerStatus, error: injuredStatusError } = await supabase
      .from('league_players_user')
      .select('is_injured')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('player_id', injuredPlayerId)
      .single();

    if (injuredStatusError) throw injuredStatusError;

    if (!injuredPlayerStatus?.is_injured) {
      return {
        success: false,
        error: { message: 'Ce joueur n’est pas sur LTIR.' }
      };
    }

    let injuredSalary = 0;
    const rosterSalaryTable = isGoalie ? 'getmyteamgoalie' : 'getmyteamplayer';
    const { data: rosterSalaryData, error: rosterSalaryError } = await supabase
      .from(rosterSalaryTable)
      .select('salary')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('playerid', injuredPlayerId)
      .single();

    if (rosterSalaryError && rosterSalaryError.code !== 'PGRST116') throw rosterSalaryError;

    if (rosterSalaryData?.salary != null) {
      injuredSalary = Number(rosterSalaryData.salary) || 0;
    } else if (isGoalie) {
      const { data: goalieSalaryData, error: goalieSalaryError } = await supabase
        .from('goalie')
        .select('salary')
        .eq('playerid', injuredPlayerId)
        .single();

      if (goalieSalaryError) throw goalieSalaryError;
      injuredSalary = Number(goalieSalaryData?.salary || 0);
    } else {
      const { data: playerSalaryData, error: playerSalaryError } = await supabase
        .from('player')
        .select('salary')
        .eq('playerid', injuredPlayerId)
        .single();

      if (playerSalaryError) throw playerSalaryError;
      injuredSalary = Number(playerSalaryData?.salary || 0);
    }

    if (enforceSalaryCap) {
      const { data: leagueSettings, error: settingsError } = await supabase
        .from('league_settings')
        .select('salary_cap, max_salary_cap, salary_cap_reserve, max_salary_cap_reserve')
        .eq('league_id', leagueId)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

      const destinationIsMain = destination === 'MAIN';

      if (destinationIsMain && leagueSettings?.salary_cap && leagueSettings.max_salary_cap != null) {
        const { data: currentMainCap, error: mainCapError } = await supabase
          .from('get_salary_cap_per_team')
          .select('salary_cap')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .eq('isnhl', true)
          .single();

        if (mainCapError && mainCapError.code !== 'PGRST116') throw mainCapError;

        const currentMainSalary = currentMainCap?.salary_cap || 0;
        const newMainSalary = currentMainSalary + injuredSalary;
        if (newMainSalary > leagueSettings.max_salary_cap) {
          const excess = newMainSalary - leagueSettings.max_salary_cap;
          return {
            success: false,
            error: {
              message: `Réactivation impossible: ce mouvement dépasserait la masse salariale du club principal de ${Math.round(excess).toLocaleString()}$.`
            }
          };
        }
      }

      if (!destinationIsMain && leagueSettings?.salary_cap_reserve && leagueSettings.max_salary_cap_reserve != null) {
        const { data: currentReserveCap, error: reserveCapError } = await supabase
          .from('get_salary_cap_per_team')
          .select('salary_cap')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .eq('isnhl', false)
          .single();

        if (reserveCapError && reserveCapError.code !== 'PGRST116') throw reserveCapError;

        const currentReserveSalary = currentReserveCap?.salary_cap || 0;
        const newReserveSalary = currentReserveSalary + injuredSalary;
        if (newReserveSalary > leagueSettings.max_salary_cap_reserve) {
          const excess = newReserveSalary - leagueSettings.max_salary_cap_reserve;
          return {
            success: false,
            error: {
              message: `Réactivation impossible: ce mouvement dépasserait la masse salariale des réserves de ${Math.round(excess).toLocaleString()}$.`
            }
          };
        }
      }
    }

    const { error: updateError } = await supabase
      .from('league_players_user')
      .update({
        is_injured: false,
        isnhl: destination === 'MAIN'
      })
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('player_id', injuredPlayerId);

    if (updateError) throw updateError;

    return { success: true };
  } catch (error: any) {
    console.error('Error reactivating injured player directly:', error);
    return {
      success: false,
      error: { message: error.message || 'Erreur lors de la réactivation du joueur' }
    };
  }
}

// Fonction pour réactiver un joueur blessé avec permutation (sans coût de rétrogradation)
export async function reactivateInjuredWithSwap(
  leagueId: number,
  userId: number,
  injuredPlayerId: number,
  swapPlayerId: number,
  isGoalie: boolean,
  destination: 'MAIN' | 'RESERVES' = 'MAIN'
) {
  try {
    // 1. Récupérer les paramètres de la ligue
    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('salary_cap, max_salary_cap, salary_cap_reserve, max_salary_cap_reserve, number_foward, number_def, number_goalie, number_reserve')
      .eq('league_id', leagueId)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

    // 2. Récupérer les statuts des deux joueurs
    const { data: injuredPlayerData, error: injuredError } = await supabase
      .from('league_players_user')
      .select('isnhl, is_scratch, is_injured')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('player_id', injuredPlayerId)
      .single();

    if (injuredError) throw injuredError;

    const { data: swapPlayerData, error: swapError } = await supabase
      .from('league_players_user')
      .select('isnhl, is_scratch, is_injured')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('player_id', swapPlayerId)
      .single();

    if (swapError) throw swapError;

    // 3. Validations
    if (swapPlayerData.is_scratch) {
      return {
        success: false,
        error: { message: 'Impossible d\'échanger avec un joueur scratch.' }
      };
    }

    if (swapPlayerData.is_injured) {
      return {
        success: false,
        error: { message: 'Impossible d\'échanger avec un joueur blessé.' }
      };
    }

    // 4. Récupérer les salaires et positions
    let injuredSalary = 0;
    let swapSalary = 0;
    let injuredPosition = '';
    let swapPosition = '';

    if (isGoalie) {
      const { data: injuredGoalie } = await supabase
        .from('goalie')
        .select('salary')
        .eq('playerid', injuredPlayerId)
        .single();
      
      const { data: swapGoalie } = await supabase
        .from('goalie')
        .select('salary')
        .eq('playerid', swapPlayerId)
        .single();

      injuredSalary = injuredGoalie?.salary || 0;
      swapSalary = swapGoalie?.salary || 0;
      injuredPosition = 'G';
      swapPosition = 'G';
    } else {
      const { data: injuredPlayer } = await supabase
        .from('player')
        .select('salary, positioncode')
        .eq('playerid', injuredPlayerId)
        .single();
      
      const { data: swapPlayer } = await supabase
        .from('player')
        .select('salary, positioncode')
        .eq('playerid', swapPlayerId)
        .single();

      injuredSalary = injuredPlayer?.salary || 0;
      swapSalary = swapPlayer?.salary || 0;
      injuredPosition = injuredPlayer?.positioncode || '';
      swapPosition = swapPlayer?.positioncode || '';
    }

    // 5. Vérifier les plafonds salariaux après la permutation
    const { data: mainCapData } = await supabase
      .from('get_salary_cap_per_team')
      .select('salary_cap')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('isnhl', true)
      .single();

    const { data: reserveCapData } = await supabase
      .from('get_salary_cap_per_team')
      .select('salary_cap')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('isnhl', false)
      .single();

    const currentMainSalary = mainCapData?.salary_cap || 0;
    const currentReserveSalary = reserveCapData?.salary_cap || 0;

    // Calculer les nouveaux plafonds après la permutation
    // Le joueur blessé va à la destination choisie
    // Le joueur swap va TOUJOURS aux réserves
    let newMainSalary = currentMainSalary;
    let newReserveSalary = currentReserveSalary;

    const injuredGoesToMain = destination === 'MAIN';

    // Retirer le salaire du joueur swap de sa position actuelle et l'ajouter aux réserves
    if (swapPlayerData.isnhl) {
      // Le joueur swap quitte le club principal pour les réserves
      newMainSalary = currentMainSalary - swapSalary;
      newReserveSalary = currentReserveSalary + swapSalary;
    }
    // Sinon le joueur swap est déjà en réserve, il y reste (pas de changement)

    // Gérer le joueur blessé selon sa destination choisie
    if (injuredGoesToMain) {
      // Le joueur blessé va au club principal
      newMainSalary += injuredSalary;
      // S'il était en réserve avant, le retirer des réserves
      if (!injuredPlayerData.isnhl) {
        newReserveSalary -= injuredSalary;
      }
    } else {
      // Le joueur blessé va aux réserves
      newReserveSalary += injuredSalary;
      // S'il était au club principal avant, le retirer du principal
      if (injuredPlayerData.isnhl) {
        newMainSalary -= injuredSalary;
      }
    }

    // Vérifier les limites de plafond salarial
    if (leagueSettings?.salary_cap && leagueSettings.salary_cap > 0) {
      if (newMainSalary > leagueSettings.max_salary_cap) {
        return {
          success: false,
          error: { message: `La permutation dépasserait le plafond salarial du club principal (${newMainSalary.toLocaleString()}$ / ${leagueSettings.max_salary_cap.toLocaleString()}$).` }
        };
      }
    }

    // NOTE: Pour la réactivation avec permutation, on permet de dépasser le plafond salarial des réserves
    // L'utilisateur pourra ajuster son alignement par la suite
    /*
    if (leagueSettings?.salary_cap_reserve && leagueSettings.salary_cap_reserve > 0) {
      if (newReserveSalary > leagueSettings.max_salary_cap_reserve) {
        return {
          success: false,
          error: { message: `La permutation dépasserait le plafond salarial des réserves (${newReserveSalary.toLocaleString()}$ / ${leagueSettings.max_salary_cap_reserve.toLocaleString()}$).` }
        };
      }
    }
    */

    // 6. Vérifier les limites d'effectif pour TOUTES les situations
    // Avec la nouvelle logique : joueur blessé prend la place du swap, swap va toujours aux réserves
    if (!isGoalie) {
      const { data: rosterCounts } = await supabase
        .from('get_number_type_player_per_team')
        .select('number_of_fowards, number_of_defenders, number_of_fowards_reserve, number_of_defenders_reserve')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .single();

      const currentForwards = rosterCounts?.number_of_fowards || 0;
      const currentDefenders = rosterCounts?.number_of_defenders || 0;
      const currentForwardsReserve = rosterCounts?.number_of_fowards_reserve || 0;
      const currentDefendersReserve = rosterCounts?.number_of_defenders_reserve || 0;

      // Déterminer où va le joueur blessé (destination choisie par l'utilisateur)
      const injuredGoingToMain = destination === 'MAIN';
      
      // Calculer les nouveaux effectifs
      let newMainForwards = currentForwards;
      let newMainDefenders = currentDefenders;
      let newReserveForwards = currentForwardsReserve;
      let newReserveDefenders = currentDefendersReserve;
      
      // Le joueur swap va TOUJOURS aux réserves
      if (swapPlayerData.isnhl) {
        // Le joueur swap quitte le principal et va aux réserves
        if (['C', 'L', 'R'].includes(swapPosition)) {
          newMainForwards--;
          newReserveForwards++;
        } else if (swapPosition === 'D') {
          newMainDefenders--;
          newReserveDefenders++;
        }
      }
      
      // Le joueur blessé va à sa destination choisie
      if (injuredGoingToMain) {
        // Le joueur blessé va au club principal
        if (['C', 'L', 'R'].includes(injuredPosition)) {
          newMainForwards++;
          // S'il était en réserve, le retirer des réserves
          if (!injuredPlayerData.isnhl) {
            newReserveForwards--;
          }
        } else if (injuredPosition === 'D') {
          newMainDefenders++;
          // S'il était en réserve, le retirer des réserves
          if (!injuredPlayerData.isnhl) {
            newReserveDefenders--;
          }
        }
      } else {
        // Le joueur blessé va aux réserves
        if (['C', 'L', 'R'].includes(injuredPosition)) {
          newReserveForwards++;
          // S'il était au principal, le retirer du principal
          if (injuredPlayerData.isnhl) {
            newMainForwards--;
          }
        } else if (injuredPosition === 'D') {
          newReserveDefenders++;
          // S'il était au principal, le retirer du principal
          if (injuredPlayerData.isnhl) {
            newMainDefenders--;
          }
        }
      }
      
      // Valider les limites du club principal
      if (newMainDefenders > (leagueSettings?.number_def || 6)) {
        return {
          success: false,
          error: { message: `La limite de défenseurs au club principal serait dépassée (${newMainDefenders} / ${leagueSettings?.number_def || 6}).` }
        };
      }
      if (newMainForwards > (leagueSettings?.number_foward || 12)) {
        return {
          success: false,
          error: { message: `La limite d'attaquants au club principal serait dépassée (${newMainForwards} / ${leagueSettings?.number_foward || 12}).` }
        };
      }
      
      // Valider les réserves - TOTAL uniquement (pas par position)
      // On doit aussi compter les gardiens en réserve pour le total
      // NOTE: Pour la réactivation avec permutation, on permet de dépasser la limite des réserves
      // L'utilisateur pourra ajuster son alignement par la suite
      /*
      const { data: goalieReserveCount } = await supabase
        .from('get_number_type_goalie_per_team')
        .select('number_of_goalie_reserve')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .single();

      const currentGoaliesReserve = goalieReserveCount?.number_of_goalie_reserve || 0;
      const newTotalReserve = newReserveForwards + newReserveDefenders + currentGoaliesReserve;
      
      if (leagueSettings?.number_reserve && newTotalReserve > leagueSettings.number_reserve) {
        return {
          success: false,
          error: { message: `La limite totale des réserves serait dépassée (${newTotalReserve} / ${leagueSettings.number_reserve}).` }
        };
      }
      */
    } else {
      // Pour les gardiens
      const { data: goalieCount } = await supabase
        .from('get_number_type_goalie_per_team')
        .select('number_of_goalie, number_of_goalie_reserve')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .single();

      const currentGoalies = goalieCount?.number_of_goalie || 0;
      const currentGoaliesReserve = goalieCount?.number_of_goalie_reserve || 0;
      
      // Déterminer où va le joueur blessé (destination choisie par l'utilisateur)
      const injuredGoingToMain = destination === 'MAIN';
      
      // Calculer les nouveaux effectifs
      let newMainGoalies = currentGoalies;
      let newReserveGoalies = currentGoaliesReserve;
      
      // Le joueur swap va TOUJOURS aux réserves
      if (swapPlayerData.isnhl) {
        newMainGoalies--;
        newReserveGoalies++;
      }
      
      // Le joueur blessé va à sa destination choisie
      if (injuredGoingToMain) {
        // Le joueur blessé va au club principal
        newMainGoalies++;
        // S'il était en réserve, le retirer des réserves
        if (!injuredPlayerData.isnhl) {
          newReserveGoalies--;
        }
      } else {
        // Le joueur blessé va aux réserves
        newReserveGoalies++;
        // S'il était au principal, le retirer du principal
        if (injuredPlayerData.isnhl) {
          newMainGoalies--;
        }
      }
      
      // Valider les limites du club principal
      if (newMainGoalies > (leagueSettings?.number_goalie || 2)) {
        return {
          success: false,
          error: { message: `La limite de gardiens au club principal serait dépassée (${newMainGoalies} / ${leagueSettings?.number_goalie || 2}).` }
        };
      }
      
      // Valider les réserves - TOTAL avec joueurs et gardiens
      // NOTE: Pour la réactivation avec permutation, on permet de dépasser la limite des réserves
      // L'utilisateur pourra ajuster son alignement par la suite
      /*
      const { data: playerReserveCounts } = await supabase
        .from('get_number_type_player_per_team')
        .select('number_of_fowards_reserve, number_of_defenders_reserve')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .single();

      const currentForwardsReserve = playerReserveCounts?.number_of_fowards_reserve || 0;
      const currentDefendersReserve = playerReserveCounts?.number_of_defenders_reserve || 0;
      const newTotalReserve = currentForwardsReserve + currentDefendersReserve + newReserveGoalies;
      
      if (leagueSettings?.number_reserve && newTotalReserve > leagueSettings.number_reserve) {
        return {
          success: false,
          error: { message: `La limite totale des réserves serait dépassée (${newTotalReserve} / ${leagueSettings.number_reserve}).` }
        };
      }
      */
    }

    // 7. Effectuer la permutation
    // Le joueur blessé va à la destination choisie
    // Le joueur swap va TOUJOURS aux réserves (club école)
    
    const { error: updateInjuredError } = await supabase
      .from('league_players_user')
      .update({ 
        is_injured: false,
        isnhl: destination === 'MAIN'  // Va à la destination choisie par l'utilisateur
      })
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('player_id', injuredPlayerId);

    if (updateInjuredError) throw updateInjuredError;

    const { error: updateSwapError } = await supabase
      .from('league_players_user')
      .update({ 
        isnhl: false  // Va TOUJOURS aux réserves (club école)
      })
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('player_id', swapPlayerId);

    if (updateSwapError) throw updateSwapError;

    return { success: true };
  } catch (error: any) {
    console.error('Error reactivating injured player with swap:', error);
    return {
      success: false,
      error: { message: error.message || 'Erreur lors de la réactivation avec permutation' }
    };
  }
}

// Standard Flexible : réactive un joueur LTIR quand l'équipe est pleine en remplaçant un joueur du club principal.
// Aucun compteur de signature ni de rétrogradation n'est incrémenté.
// - Sans club école (number_reserve = 0) : le joueur remplacé est libéré après la réactivation.
// - Avec club école : le joueur remplacé va en réserve, sauf si la limite de réserve serait dépassée (blocage).
export async function reactivateInjuredReplacingPlayer(
  leagueId: number,
  userId: number,
  injuredPlayerId: number,
  replacedPlayerId: number,
  isGoalie: boolean
): Promise<{ success: boolean; error?: any; outcome?: 'RELEASED' | 'RESERVES' }> {
  try {
    if (injuredPlayerId === replacedPlayerId) {
      return { success: false, error: { message: 'Le joueur remplacé doit être différent du joueur réactivé.' } };
    }

    const { data: leagueSettings, error: settingsError } = await supabase
      .from('league_settings')
      .select('salary_cap, max_salary_cap, salary_cap_reserve, max_salary_cap_reserve, number_foward, number_def, number_goalie, number_reserve')
      .eq('league_id', leagueId)
      .single();
    if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

    const rosterTable = isGoalie ? 'getmyteamgoalie' : 'getmyteamplayer';
    const rosterColumns = isGoalie ? 'playerid, salary' : 'playerid, salary, positioncode';
    const { data: rosterRows, error: rosterError } = await supabase
      .from(rosterTable)
      .select(rosterColumns)
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .in('playerid', [injuredPlayerId, replacedPlayerId]);
    if (rosterError) throw rosterError;

    const injuredRow: any = (rosterRows as any[] | null)?.find((r) => r.playerid === injuredPlayerId);
    const replacedRow: any = (rosterRows as any[] | null)?.find((r) => r.playerid === replacedPlayerId);
    if (!injuredRow || !replacedRow) {
      return { success: false, error: { message: 'Joueur introuvable dans votre équipe.' } };
    }

    const groupOf = (code?: string) => (code === 'D' ? 'D' : 'F');
    if (!isGoalie && groupOf(injuredRow.positioncode) !== groupOf(replacedRow.positioncode)) {
      return { success: false, error: { message: 'Le joueur remplacé doit être de la même position que le joueur réactivé.' } };
    }

    const { data: statusRows, error: statusError } = await supabase
      .from('league_players_user')
      .select('player_id, isnhl, is_scratch, is_injured')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .in('player_id', [injuredPlayerId, replacedPlayerId]);
    if (statusError) throw statusError;

    const injuredStatus = statusRows?.find((r: any) => r.player_id === injuredPlayerId);
    const replacedStatus = statusRows?.find((r: any) => r.player_id === replacedPlayerId);
    if (!injuredStatus?.is_injured) {
      return { success: false, error: { message: 'Ce joueur n’est pas sur LTIR.' } };
    }
    if (!replacedStatus || !replacedStatus.isnhl || replacedStatus.is_scratch || replacedStatus.is_injured) {
      return { success: false, error: { message: 'Le joueur remplacé doit faire partie de votre club principal (ni scratch, ni blessé).' } };
    }

    const injuredSalary = Number(injuredRow.salary) || 0;
    const replacedSalary = Number(replacedRow.salary) || 0;

    // L'équipe doit réellement être pleine à cette position
    if (isGoalie) {
      const { data: goalieCounts } = await supabase
        .from('get_number_type_goalie_per_team')
        .select('number_of_goalie')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .single();
      const max = leagueSettings?.number_goalie || 2;
      if ((goalieCounts?.number_of_goalie || 0) < max) {
        return { success: false, error: { message: 'Votre équipe n’est pas pleine : utilisez la réactivation directe.' } };
      }
    } else {
      const { data: playerCounts } = await supabase
        .from('get_number_type_player_per_team')
        .select('number_of_defenders, number_of_fowards')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .single();
      const isDefense = injuredRow.positioncode === 'D';
      const current = isDefense ? (playerCounts?.number_of_defenders || 0) : (playerCounts?.number_of_fowards || 0);
      const max = isDefense ? (leagueSettings?.number_def || 6) : (leagueSettings?.number_foward || 12);
      if (current < max) {
        return { success: false, error: { message: 'Votre équipe n’est pas pleine à cette position : utilisez la réactivation directe.' } };
      }
    }

    const reserveLimit = Number(leagueSettings?.number_reserve) || 0;
    const hasReserveTeam = reserveLimit > 0;

    // Plafond salarial du club principal après le remplacement
    if (leagueSettings?.salary_cap && leagueSettings.max_salary_cap != null) {
      const { data: mainCap } = await supabase
        .from('get_salary_cap_per_team')
        .select('salary_cap')
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('isnhl', true)
        .single();
      const newMain = (Number(mainCap?.salary_cap) || 0) + injuredSalary - replacedSalary;
      if (newMain > leagueSettings.max_salary_cap) {
        const excess = Math.round(newMain - leagueSettings.max_salary_cap);
        return { success: false, error: { message: `Réactivation impossible: ce remplacement dépasserait la masse salariale du club principal de ${excess.toLocaleString()}$.` } };
      }
    }

    if (hasReserveTeam) {
      const [{ data: playerCounts }, { data: goalieCounts }] = await Promise.all([
        supabase
          .from('get_number_type_player_per_team')
          .select('number_of_defenders_reserve, number_of_fowards_reserve')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .single(),
        supabase
          .from('get_number_type_goalie_per_team')
          .select('number_of_goalie_reserve')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .single()
      ]);
      const currentReserve =
        (playerCounts?.number_of_defenders_reserve || 0) +
        (playerCounts?.number_of_fowards_reserve || 0) +
        (goalieCounts?.number_of_goalie_reserve || 0);
      if (currentReserve + 1 > reserveLimit) {
        return {
          success: false,
          error: { message: `Remplacement impossible: la limite de réserve serait dépassée (${currentReserve + 1} / ${reserveLimit}). Libérez d’abord un joueur de votre réserve.` }
        };
      }

      if (leagueSettings?.salary_cap_reserve && leagueSettings.max_salary_cap_reserve != null) {
        const { data: reserveCap } = await supabase
          .from('get_salary_cap_per_team')
          .select('salary_cap')
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .eq('isnhl', false)
          .single();
        const newReserve = (Number(reserveCap?.salary_cap) || 0) + replacedSalary;
        if (newReserve > leagueSettings.max_salary_cap_reserve) {
          const excess = Math.round(newReserve - leagueSettings.max_salary_cap_reserve);
          return { success: false, error: { message: `Remplacement impossible: la masse salariale des réserves serait dépassée de ${excess.toLocaleString()}$.` } };
        }
      }
    } else {
      const movementCheck = await ensureNoMovementAllowed(leagueId);
      if (movementCheck.blocked) {
        return { success: false, error: { message: movementCheck.message || "Les mouvements d'effectif sont actuellement bloqués." } };
      }
    }

    const { error: reactivateError } = await supabase
      .from('league_players_user')
      .update({ is_injured: false, isnhl: true })
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .eq('player_id', injuredPlayerId);
    if (reactivateError) throw reactivateError;

    if (hasReserveTeam) {
      const { error: demoteError } = await supabase
        .from('league_players_user')
        .update({ isnhl: false })
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('player_id', replacedPlayerId);
      if (demoteError) {
        await supabase
          .from('league_players_user')
          .update({ is_injured: true, isnhl: injuredStatus.isnhl })
          .eq('league_id', leagueId)
          .eq('user_id', userId)
          .eq('player_id', injuredPlayerId);
        throw demoteError;
      }
      return { success: true, outcome: 'RESERVES' };
    }

    const released = await releasePlayer(replacedPlayerId, userId, leagueId);
    if (!released.success) {
      await supabase
        .from('league_players_user')
        .update({ is_injured: true, isnhl: injuredStatus.isnhl })
        .eq('league_id', leagueId)
        .eq('user_id', userId)
        .eq('player_id', injuredPlayerId);
      throw released.error;
    }
    return { success: true, outcome: 'RELEASED' };
  } catch (error: any) {
    console.error('Error reactivating injured player by replacing a player:', error);
    return {
      success: false,
      error: { message: error?.message || 'Erreur lors de la réactivation du joueur' }
    };
  }
}

// ==================== Salary Retention Functions ====================

/**
 * Get the total salary retention debt that a team must pay
 */
export async function getTeamRetentionDebt(leagueId: number, userId: number) {
  try {
    const { data, error } = await supabase.rpc('get_team_retention_debt', {
      p_league_id: leagueId,
      p_user_id: userId
    });

    if (error) throw error;

    return {
      totalRetainedSalary: data[0]?.total_retained_salary || 0,
      numRetainedPlayers: data[0]?.num_retained_players || 0,
      retainedPlayers: data[0]?.retained_players || []
    };
  } catch (error: any) {
    console.error('Error fetching team retention debt:', error);
    return {
      totalRetainedSalary: 0,
      numRetainedPlayers: 0,
      retainedPlayers: []
    };
  }
}

/**
 * Create salary retention entries when a trade with retention is accepted
 * This should be called after a trade is fully accepted
 */
export async function createSalaryRetentionFromTrade(tradeId: number, leagueId: number) {
  try {
    const { error } = await supabase.rpc('create_salary_retention_from_trade', {
      p_trade_id: tradeId,
      p_league_id: leagueId
    });

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error creating salary retention from trade:', error);
    return {
      success: false,
      error: { message: error.message || 'Erreur lors de la création de la rétention de salaire' }
    };
  }
}

/**
 * Deactivate salary retention when a player is traded again
 * 
 * ⚠️ IMPORTANT: Do NOT call this function when a player is released!
 * The team with retention debt must continue paying even if the player becomes a free agent.
 * 
 * This function should ONLY be called when:
 * - A player with existing retention is traded again (new team takes over)
 * - End of season cleanup (use deactivate_all_retentions_for_season instead)
 */
export async function deactivateSalaryRetention(leagueId: number, playerId: number, currentOwnerUserId: number) {
  try {
    const { error } = await supabase.rpc('deactivate_salary_retention', {
      p_league_id: leagueId,
      p_player_id: playerId,
      p_current_owner_user_id: currentOwnerUserId
    });

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error deactivating salary retention:', error);
    return {
      success: false,
      error: { message: error.message || 'Erreur lors de la désactivation de la rétention de salaire' }
    };
  }
}

export type OutreachChannel = 'podcast' | 'youtube' | 'restaurant';
export type OutreachContactStatus = 'a_contacter' | 'contacte' | 'relance_1' | 'relance_2' | 'interesse' | 'partenariat' | 'sans_reponse';
export type OutreachPriority = 'basse' | 'moyenne' | 'haute';
export type OutreachInteractionType = 'email' | 'dm' | 'appel' | 'rencontre' | 'note';
export type OutreachInteractionOutcome = 'en_attente' | 'positif' | 'neutre' | 'negatif' | 'sans_reponse';

export interface OutreachTemplateRecord {
  id: number;
  league_id: number;
  user_id: number;
  channel: OutreachChannel;
  title: string;
  subject: string | null;
  message: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface OutreachContactRecord {
  id: number;
  league_id: number;
  user_id: number;
  channel: OutreachChannel;
  name: string;
  platform_name: string | null;
  role_title: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  status: OutreachContactStatus;
  priority: OutreachPriority;
  next_follow_up_at: string | null;
  notes: string | null;
  last_contact_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OutreachInteractionRecord {
  id: number;
  contact_id: number;
  league_id: number;
  user_id: number;
  template_id: number | null;
  interaction_type: OutreachInteractionType;
  subject: string | null;
  content: string;
  outcome: OutreachInteractionOutcome;
  interaction_date: string;
  follow_up_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface OutreachTemplateInput {
  id?: number;
  league_id: number;
  user_id: number;
  channel: OutreachChannel;
  title: string;
  subject?: string | null;
  message: string;
  is_active?: boolean;
}

export interface OutreachContactInput {
  id?: number;
  league_id: number;
  user_id: number;
  channel: OutreachChannel;
  name: string;
  platform_name?: string | null;
  role_title?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  status: OutreachContactStatus;
  priority: OutreachPriority;
  next_follow_up_at?: string | null;
  notes?: string | null;
}

export interface OutreachInteractionInput {
  id?: number;
  contact_id: number;
  league_id: number;
  user_id: number;
  template_id?: number | null;
  interaction_type: OutreachInteractionType;
  subject?: string | null;
  content: string;
  outcome: OutreachInteractionOutcome;
  interaction_date?: string;
  follow_up_date?: string | null;
}

export async function getOutreachTemplates(leagueId: number, userId: number) {
  try {
    const { data, error } = await supabase
      .from('outreach_templates')
      .select('*')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error && error.code !== 'PGRST116') throw error;

    return {
      data: (data ?? []) as OutreachTemplateRecord[],
      error: null,
    };
  } catch (error: any) {
    console.error('Error fetching outreach templates:', error);
    return { data: [] as OutreachTemplateRecord[], error };
  }
}

export async function saveOutreachTemplate(template: OutreachTemplateInput) {
  try {
    const payload = {
      league_id: template.league_id,
      user_id: template.user_id,
      channel: template.channel,
      title: template.title,
      subject: template.subject?.trim() || null,
      message: template.message,
      is_active: template.is_active ?? true,
    };

    const query = template.id
      ? supabase
          .from('outreach_templates')
          .update(payload)
          .eq('id', template.id)
          .eq('league_id', template.league_id)
          .eq('user_id', template.user_id)
      : supabase.from('outreach_templates').insert(payload);

    const { data, error } = await query.select('*').single();

    if (error) throw error;

    return { success: true, data: data as OutreachTemplateRecord, error: null };
  } catch (error: any) {
    console.error('Error saving outreach template:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Erreur lors de la sauvegarde du modele outreach' },
    };
  }
}

export async function getOutreachContacts(leagueId: number, userId: number) {
  try {
    const { data, error } = await supabase
      .from('crm_contacts')
      .select('*')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error && error.code !== 'PGRST116') throw error;

    return {
      data: (data ?? []) as OutreachContactRecord[],
      error: null,
    };
  } catch (error: any) {
    console.error('Error fetching outreach contacts:', error);
    return { data: [] as OutreachContactRecord[], error };
  }
}

export async function saveOutreachContact(contact: OutreachContactInput) {
  try {
    const payload = {
      league_id: contact.league_id,
      user_id: contact.user_id,
      channel: contact.channel,
      name: contact.name,
      platform_name: contact.platform_name?.trim() || null,
      role_title: contact.role_title?.trim() || null,
      email: contact.email?.trim() || null,
      phone: contact.phone?.trim() || null,
      city: contact.city?.trim() || null,
      status: contact.status,
      priority: contact.priority,
      next_follow_up_at: contact.next_follow_up_at || null,
      notes: contact.notes?.trim() || null,
    };

    const query = contact.id
      ? supabase
          .from('crm_contacts')
          .update({
            ...payload,
            last_contact_at: ['contacte', 'relance_1', 'relance_2', 'interesse', 'partenariat'].includes(contact.status) ? new Date().toISOString() : null,
          })
          .eq('id', contact.id)
          .eq('league_id', contact.league_id)
          .eq('user_id', contact.user_id)
      : supabase.from('crm_contacts').insert(payload);

    const { data, error } = await query.select('*').single();

    if (error) throw error;

    return { success: true, data: data as OutreachContactRecord, error: null };
  } catch (error: any) {
    console.error('Error saving outreach contact:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Erreur lors de la sauvegarde du contact' },
    };
  }
}

export async function getOutreachInteractions(contactId: number, leagueId: number, userId: number) {
  try {
    const { data, error } = await supabase
      .from('crm_interactions')
      .select('*')
      .eq('contact_id', contactId)
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .order('interaction_date', { ascending: false });

    if (error && error.code !== 'PGRST116') throw error;

    return {
      data: (data ?? []) as OutreachInteractionRecord[],
      error: null,
    };
  } catch (error: any) {
    console.error('Error fetching outreach interactions:', error);
    return { data: [] as OutreachInteractionRecord[], error };
  }
}

export async function saveOutreachInteraction(interaction: OutreachInteractionInput) {
  try {
    const payload = {
      contact_id: interaction.contact_id,
      league_id: interaction.league_id,
      user_id: interaction.user_id,
      template_id: interaction.template_id ?? null,
      interaction_type: interaction.interaction_type,
      subject: interaction.subject?.trim() || null,
      content: interaction.content,
      outcome: interaction.outcome,
      interaction_date: interaction.interaction_date || new Date().toISOString(),
      follow_up_date: interaction.follow_up_date || null,
    };

    const query = interaction.id
      ? supabase
          .from('crm_interactions')
          .update(payload)
          .eq('id', interaction.id)
          .eq('league_id', interaction.league_id)
          .eq('user_id', interaction.user_id)
      : supabase.from('crm_interactions').insert(payload);

    const { data, error } = await query.select('*').single();

    if (error) throw error;

    const { error: contactError } = await supabase
      .from('crm_contacts')
      .update({
        last_contact_at: payload.interaction_date,
        next_follow_up_at: payload.follow_up_date,
        updated_at: new Date().toISOString(),
      })
      .eq('id', interaction.contact_id)
      .eq('league_id', interaction.league_id)
      .eq('user_id', interaction.user_id);

    if (contactError) {
      console.warn('Unable to sync contact after interaction save:', contactError);
    }

    return { success: true, data: data as OutreachInteractionRecord, error: null };
  } catch (error: any) {
    console.error('Error saving outreach interaction:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Erreur lors de la sauvegarde du suivi outreach' },
    };
  }
}

// ─── Quick Player Search (header autocomplete) ───────────────────────────────

export interface QuickPlayerResult {
  playerid: number;
  fullname: string;
  positioncode: string;
  teamabbrevs: string;
  status: 'league' | 'freeAgent' | 'global';
}

export async function quickSearchPlayers(
  term: string,
  leagueId?: number,
  options?: { isStandardFlexible?: boolean; userId?: number | null }
): Promise<{ data: QuickPlayerResult[] | null; error: any }> {
  try {
    const cleaned = term.trim();
    if (!cleaned || cleaned.length < 2) return { data: [], error: null };

    if (leagueId) {
      const isStandardFlexible = options?.isStandardFlexible === true;
      const currentUserId = options?.userId ?? null;

      // 4 requêtes parallèles : joueurs de ligue + agents libres
      const [leagueSkatersRes, leagueGoaliesRes, faSkatersRes, faGoaliesRes] = await Promise.all([
        supabase
          .from('getmyteamplayer')
          .select('playerid, skaterfullname, positioncode, teamabbrevs')
          .eq('league_id', leagueId)
          .ilike('skaterfullname', `%${cleaned}%`)
          .limit(5),
        supabase
          .from('getmyteamgoalie')
          .select('playerid, goaliefullname, teamabbrevs')
          .eq('league_id', leagueId)
          .ilike('goaliefullname', `%${cleaned}%`)
          .limit(5),
        isStandardFlexible && currentUserId
          ? supabase
            .rpc('get_free_agents_standard', { p_league_id: leagueId, p_user_id: currentUserId })
            .select('playerid, skaterfullname, positioncode, teamabbrevs')
            .ilike('skaterfullname', `%${cleaned}%`)
            .limit(5)
          : supabase
            .rpc('get_free_agents_by_league', { p_league_id: leagueId })
            .select('playerid, skaterfullname, positioncode, teamabbrevs')
            .ilike('skaterfullname', `%${cleaned}%`)
            .limit(5),
        isStandardFlexible && currentUserId
          ? supabase
            .rpc('get_free_agents_standard_goalie', { p_league_id: leagueId, p_user_id: currentUserId })
            .select('playerid, goaliefullname, teamabbrevs')
            .ilike('goaliefullname', `%${cleaned}%`)
            .limit(5)
          : supabase
            .rpc('get_free_agents_by_league_goalie', { p_league_id: leagueId })
            .select('playerid, goaliefullname, teamabbrevs')
            .ilike('goaliefullname', `%${cleaned}%`)
            .limit(5),
      ]);

      const leagueSkaters: QuickPlayerResult[] =
        (leagueSkatersRes.data || []).map((p: any) => ({
          playerid: p.playerid,
          fullname: p.skaterfullname,
          positioncode: p.positioncode || '',
          teamabbrevs: p.teamabbrevs || '',
          status: 'league' as const,
        }));

      const leagueGoalies: QuickPlayerResult[] =
        (leagueGoaliesRes.data || []).map((g: any) => ({
          playerid: g.playerid,
          fullname: g.goaliefullname,
          positioncode: 'G',
          teamabbrevs: g.teamabbrevs || '',
          status: 'league' as const,
        }));

      const faSkatersData = Array.isArray(faSkatersRes.data)
        ? faSkatersRes.data
        : faSkatersRes.data
          ? [faSkatersRes.data]
          : [];

      const faSkaters: QuickPlayerResult[] =
        faSkatersData.map((p: any) => ({
          playerid: p.playerid,
          fullname: p.skaterfullname,
          positioncode: p.positioncode || '',
          teamabbrevs: p.teamabbrevs || '',
          status: 'freeAgent' as const,
        }));

      const faGoaliesData = Array.isArray(faGoaliesRes.data)
        ? faGoaliesRes.data
        : faGoaliesRes.data
          ? [faGoaliesRes.data]
          : [];

      const faGoalies: QuickPlayerResult[] =
        faGoaliesData.map((g: any) => ({
          playerid: g.playerid,
          fullname: g.goaliefullname,
          positioncode: 'G',
          teamabbrevs: g.teamabbrevs || '',
          status: 'freeAgent' as const,
        }));

      // Dédupliquer par playerid (un joueur ne peut être que dans une seule catégorie)
      const all = [...leagueSkaters, ...leagueGoalies, ...faSkaters, ...faGoalies];
      const seen = new Set<number>();
      const unique = all.filter((r) => {
        if (seen.has(r.playerid)) return false;
        seen.add(r.playerid);
        return true;
      });

      return { data: unique, error: null };
    } else {
      // Pas de ligue sélectionnée — recherche globale dans les vues du jour
      const [skatersRes, goaliesRes] = await Promise.all([
        supabase
          .from('show_player_onlycurrentday')
          .select('playerid, skaterfullname, positioncode, teamabbrevs')
          .ilike('skaterfullname', `%${cleaned}%`)
          .limit(8),
        supabase
          .from('show_goalie_onlycurrentday')
          .select('playerid, goaliefullname, teamabbrevs')
          .ilike('goaliefullname', `%${cleaned}%`)
          .limit(4),
      ]);

      const skaters: QuickPlayerResult[] =
        (skatersRes.data || []).map((p: any) => ({
          playerid: p.playerid,
          fullname: p.skaterfullname,
          positioncode: p.positioncode || '',
          teamabbrevs: p.teamabbrevs || '',
          status: 'global' as const,
        }));

      const goalies: QuickPlayerResult[] =
        (goaliesRes.data || []).map((g: any) => ({
          playerid: g.playerid,
          fullname: g.goaliefullname,
          positioncode: 'G',
          teamabbrevs: g.teamabbrevs || '',
          status: 'global' as const,
        }));

      const all = [...skaters, ...goalies];
      const seen = new Set<number>();
      const unique = all.filter((r) => {
        if (seen.has(r.playerid)) return false;
        seen.add(r.playerid);
        return true;
      });

      return { data: unique, error: null };
    }
  } catch (error: any) {
    console.error('quickSearchPlayers error:', error);
    return { data: null, error };
  }
}
